import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { UnsavedChangesModal } from '../components/unsaved-changes-modal.component';
import { createPreliminaryInvoiceApi } from '../features/invoice/invoice.service';
import { Helmet } from "react-helmet";
import { CreateInvoice } from "../components/create-invoice.component";
import { InvoiceNavigation } from "../components/invoice-navigation.component";
import { InvoiceFooter } from "../components/invoice-footer.component";
import { InvoicePreviewModal } from "../components/invoice-preview-modal.component";
import { AccountAccess } from "../components/account-access.component";
import { Footer } from "../components/footer.component";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { getTaxFreeArticleNumber, getTaxFreeArticleUnit } from "../constants/invoice-deductions.constants";
import { transformDeductionToWorkingHour } from "../utils/deduction.utils";
import "../scss/main.scss";
import SectionHeader from "../components/common";
import { useTranslation } from "react-i18next";
import { LOCALES } from "../../common/constants";
import { getSchemaMarkup } from "../../common/FunctionUtils";
import SEOMetadataComponent from "../components/seo-metadata.component";
import { fetchGlobalData } from "../../redux/globalData/globalDataSlice";
import { useAppDispatch, useAppSelector } from "../hooks/useRedux";
import { createInvoice, createPreliminaryInvoice, createDraftInvoice } from "../../v2/features/invoice/invoice.slice";
import { CreateInvoicePayload, CreateDraftInvoicePayload } from "../../v2/features/invoice/invoice.service";
import { toast } from "react-toastify";
import Header from '../components/header/Header';
import LoginWithBankId from './bankId-login/bankId-signin';
import BankIDAuth from '../components/bank-id-auth';
import { SaveInvoiceModal } from '../components/save-invoice-modal.component';
import { updateUser, getUserOnboarding } from '../../redux/auth/authSlice';
import { updateUserOnboarding } from '../features/onboarding/onboarding.service';
import { fetchBankIdVerificationStatus, LatestDocumentStatus } from '../features/bankid/bankid.slice';

import Sidebar from "../components/Sidebar";
import apiCall from "../../api";
import { InvoiceTour, TourStep } from "../components/invoice-tour/invoice-tour.component";
import { InvoiceWelcomeModal } from "../components/invoice-tour/invoice-welcome-modal.component";

/* Safely parse the `onBoardingSteps` field from the /useronboarding GET
   response — it arrives as a JSON string. Returns {} on missing/malformed. */
const parseOnboardingSteps = (raw: unknown): Record<string, unknown> => {
  if (typeof raw !== 'string' || !raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch { return {}; }
};

export const CreateInvoicePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [isPreviousInvoicingTool, setIsPreviousInvoicingTool] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // Wizard step tracking (reported by CreateInvoice) — used to show the Send/Draft footer on the Review step only
  const [wizardStep, setWizardStep] = useState(0);
  // Active step's live context (title + subtitle) — shown in the footer in place
  // of Grand Total on mobile.
  const [activeStepTitle, setActiveStepTitle] = useState('');
  const [activeStepSubtitle, setActiveStepSubtitle] = useState('');
  const [wizardTotalSteps, setWizardTotalSteps] = useState(6);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [preId, setPreId] = useState<string | null>(null);
  const [createdDateFromUrl, setCreatedDateFromUrl] = useState<string | null>(null);
  const [invoiceNumberFromUrl, setInvoiceNumberFromUrl] = useState<number>(0);

  // Reference to store invoice data from CreateInvoice component
  const invoiceDataRef = useRef<any>(null);

  // Holds the wizard's "Back" handler so the final-step footer can trigger it
  const wizardBackRef = useRef<(() => void) | null>(null);

  // Reference to store validation functions for service descriptions (keyed by freelancerId)
  const validateServiceDescriptionsRef = useRef<{ [freelancerId: string]: () => boolean } | undefined>(undefined);

  // Reference to store date validation functions (keyed by freelancerId)
  const validateDatesRef = useRef<{ [freelancerId: string]: () => boolean } | undefined>(undefined);

  // Ref to track previous URL mode (excluding lang parameter)
  const previousUrlModeRef = useRef<{
    hasEdit: boolean;
    hasPreId: boolean;
    hasTimesheet: boolean;
    hasInvoiceNumber: boolean;
  } | null>(null);

  // State to track calculated sub amount from ItemDetails
  const [calculatedSubAmount, setCalculatedSubAmount] = useState<any>(null);

  // State to track deductions by freelancer
  const [deductionsByFreelancer, setDeductionsByFreelancer] = useState<any>({});
  
  // State to track current due date from preferences
  const [currentDueDate, setCurrentDueDate] = useState<string | null>(null);

  // State to track removed copied working hours
  const [removedCopiedWorkingHours, setRemovedCopiedWorkingHours] = useState<Set<number>>(new Set());

  // State to track copied invoice data
  const [copiedInvoiceData, setCopiedInvoiceData] = useState<any>(null);

  // 🆕 TradeLab Tracking State - Track if invoice is already synced
  const [isAlreadySynced, setIsAlreadySynced] = useState<boolean>(true);

  // Get Redux state
  const globalData = useAppSelector((state: any) => state?.globalData?.data);
  const customerState = useAppSelector((state: any) => state?.customer);
  const userDetails = useAppSelector((state: any) => state?.auth?.user);
  const { createLoading, createdInvoice, calculationData, preliminaryLoading, preliminaryInvoice, draftLoading, draftInvoice } = useAppSelector((state: any) => state?.invoice);
  
  // Get timesheet data from Redux
  const timesheetData = useAppSelector((state: any) => state?.timesheet?.timesheetData);
  const timesheetResponse = useAppSelector((state: any) => state?.timesheet);

  // Monitor and log timesheet data when it's available
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const timesheetParam = searchParams.get('timesheet');
    
    if (timesheetParam === 'true') {
      
      if (timesheetData && timesheetData.length > 0) {
      } else {
      }
    }
  }, [timesheetData, timesheetResponse, location.search]);

  // Extract URL parameters and check for edit mode
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isEditMode = searchParams.get('edit');
    const preIdParam = searchParams.get('preId');
    const invoiceNumber = searchParams.get('invoiceNumber');
    const lang = searchParams.get('lang');
    const createdDate = searchParams.get('createdDate');
    const timesheetParam = searchParams.get('timesheet');

    if (isEditMode === 'true') {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }

    // Store preId in state
    if (preIdParam) {
      setPreId(preIdParam);
    }

    // Store invoiceNumber in state (default to 0 if not present)
    if (invoiceNumber) {
      setInvoiceNumberFromUrl(parseInt(invoiceNumber, 10) || 0);
    } else {
      setInvoiceNumberFromUrl(0);
    }

    // Store createdDate in state
    if (createdDate) {
      setCreatedDateFromUrl(createdDate);
    }

    // Check if coming from timesheet and log the data
    if (timesheetParam === 'true') {
    }
  }, [location.search]);

  // Fetch global data on component mount (same as InvoiceTool.js)
  useEffect(() => {
    dispatch(fetchGlobalData() as any);
  }, [dispatch]);

  // Reset state when transitioning FROM edit/timesheet/preId TO fresh creation
  // BUT NOT when only language changes in fresh creation mode
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasEditParam = searchParams.get('edit') === 'true';
    const hasPreIdParam = searchParams.get('preId');
    const hasTimesheetParam = searchParams.get('timesheet') === 'true';
    const hasInvoiceNumber = searchParams.get('invoiceNumber');

    // Current URL mode
    const currentMode = {
      hasEdit: hasEditParam,
      hasPreId: !!hasPreIdParam,
      hasTimesheet: hasTimesheetParam,
      hasInvoiceNumber: !!hasInvoiceNumber
    };

    // Check if this is fresh creation
    const isFreshCreation = !hasEditParam && !hasPreIdParam && !hasTimesheetParam && !hasInvoiceNumber;

    // Check if we had a previous mode
    const hadPreviousMode = previousUrlModeRef.current;

    // Only reset if:
    // 1. First time (no previous mode) AND it's fresh creation
    // 2. OR transitioning FROM a special mode (edit/timesheet/preId) TO fresh creation
    let shouldReset = false;

    if (!hadPreviousMode && isFreshCreation) {
      // First load with fresh creation - reset
      shouldReset = true;
    } else if (hadPreviousMode) {
      const wasInSpecialMode = hadPreviousMode.hasEdit || hadPreviousMode.hasPreId || 
                                hadPreviousMode.hasTimesheet || hadPreviousMode.hasInvoiceNumber;
      
      // If we were in a special mode and now we're in fresh creation - reset
      if (wasInSpecialMode && isFreshCreation) {
        shouldReset = true;
      }
      
      // If mode changed (e.g., from edit to preId, or timesheet to edit) - don't reset
      // This handles navigation between different special modes
    }

    // Update previous mode
    previousUrlModeRef.current = currentMode;

    // Perform reset only if needed
    if (shouldReset) {
      // Reset Redux states
      dispatch({ type: 'invoice/resetInvoiceState' });
      // When My Clients deep-links a customer (?customerId=), DON'T wipe the
      // customer selection — customer-details will pre-select it. Just clear the
      // collaborators list so a fresh invoice doesn't inherit old freelancers.
      const hasCustomerIdParam = !!searchParams.get('customerId');
      if (hasCustomerIdParam) {
        dispatch({ type: 'customer/setSelectedFreelancersForInvoice', payload: [] });
      } else {
        dispatch({ type: 'customer/resetCustomerSelection' });
      }
      dispatch({ type: 'timesheet/resetTimesheetData' });

      // Reset local states
      setCopiedInvoiceData(null);
      setIsEditMode(false);
      setPreId(null);
      setInvoiceNumberFromUrl(0);
      setCreatedDateFromUrl(null);
      setCalculatedSubAmount(null);
      setDeductionsByFreelancer({});
      setRemovedCopiedWorkingHours(new Set());

      // Reset invoice data ref
      if (invoiceDataRef.current) {
        invoiceDataRef.current = null;
      }
    }
  }, [location.search, dispatch]);

  const handleMenuClick = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const currentLanguage = i18n.language;

  // Edit mode read straight from the URL — reliable source of truth for the
  // header/footer button conditions (Save-draft visibility + Send vs Edit Request),
  // independent of any effect-timing on the `isEditMode` state.
  const isEditModeFromUrl = React.useMemo(
    () => new URLSearchParams(location.search).get('edit') === 'true',
    [location.search]
  );
  const editMode = isEditMode || isEditModeFromUrl;

  // The guided tour is ONLY for a brand-new invoice. Hide "Start Tour" (and the
  // auto welcome modal) in edit, draft (preId), edit-request and timesheet flows.
  const isFreshInvoice = React.useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return !(sp.get('edit') === 'true')
      && !sp.get('preId')
      && !(sp.get('timesheet') === 'true')
      && !sp.get('invoiceNumber')
      && !isEditMode;
  }, [location.search, isEditMode]);

  const isEnglish = currentLanguage === "en" || currentLanguage === LOCALES.EN_US;
  const canonicalUrl = isEnglish ? `https://www.invozio.com/v2/create-invoice?lang=${LOCALES.EN_US}` : `https://www.invozio.com/v2/create-invoice?lang=${LOCALES.SV_SE}`;

  const schemaMarkup = getSchemaMarkup("create-invoice");

  // Helper function to convert date from MM/DD/YYYY to YYYY/MM/DD format
  const formatDateForAPI = (dateStr: string): string => {
    if (!dateStr) return '';

    // Check if date is already in YYYY/MM/DD or YYYY-MM-DD format
    if (dateStr.match(/^\d{4}[/-]\d{2}[/-]\d{2}$/)) {
      return dateStr.replace(/-/g, '/');
    }

    // Handle MM/DD/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      // Convert to YYYY/MM/DD
      return `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
    }

    return dateStr; // Return as-is if format not recognized
  };

  const handlePreview = async () => {
    try {

      // Get invoice data from ref
      const invoiceData = invoiceDataRef.current;
      let customerSelectedLanguage = "";
      const customerDetails = customerState?.customers.find((item:any) => item?.customerId === customerState?.selectedCustomer?.customerId);

      if (customerDetails) {
        customerSelectedLanguage = customerDetails?.language || "en-us";
      }

      if (!invoiceData) {
        toast.error("Please fill in all required invoice details");
        console.error("❌ No invoice data available");
        return;
      }

      // Get customer details
      const selectedCustomer = customerState?.selectedCustomer;
      if (!selectedCustomer || (!selectedCustomer.id && !selectedCustomer.customerId)) {
        toast.error("Please select a customer");
        console.error("❌ No customer selected");
        return;
      }

      const customerId = selectedCustomer.customerId || selectedCustomer.id;

      // Build the same payload as handleSendInvoice
      const { preferences = {}, serviceData = {}, calculatedAmounts = {}, formData = {}, pensionDataByFreelancer = {}, holidayDataByFreelancer = {}, deductionsByFreelancer = {}, selectedFreelancersForInvoice = [] } = invoiceData;

      // Collective = MORE than one freelancer (single mode keeps exactly 1: the
      // current user). Pension/holiday are then keyed by userId, not "default".
      const isCollectiveInvoice = selectedFreelancersForInvoice && selectedFreelancersForInvoice.length > 1;

      // Prepare pension data
      let pensionToSend = false;
      let pensionAmountToSend = 0;

      if (isCollectiveInvoice) {
        const allHavePension = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return pensionDataByFreelancer[freelancerId]?.pension;
        });
        pensionToSend = allHavePension;
        pensionAmountToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const amount = pensionDataByFreelancer[freelancerId]?.pensionAmount || 0;
          return sum + amount;
        }, 0);
      } else {
        const defaultPensionData = pensionDataByFreelancer["default"];
        pensionToSend = defaultPensionData?.pension || false;
        pensionAmountToSend = defaultPensionData?.pensionAmount || 0;
      }

      // Prepare holiday savings data
      let holidaySavingsToSend = false;
      let holidaySavingsFeeToSend = 0;

      if (isCollectiveInvoice) {
        const allHaveHolidaySavings = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return holidayDataByFreelancer[freelancerId]?.holidaySavings;
        });
        holidaySavingsToSend = allHaveHolidaySavings;
        holidaySavingsFeeToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const fee = holidayDataByFreelancer[freelancerId]?.holidaySavingsFee || 0;
          return sum + fee;
        }, 0);
      } else {
        const defaultHolidayData = holidayDataByFreelancer["default"];
        holidaySavingsToSend = defaultHolidayData?.holidaySavings || false;
        holidaySavingsFeeToSend = defaultHolidayData?.holidaySavingsFee || 0;
      }

      // Prepare working hours data
      let totalWorkingDates: any[] = [];
      let totalAmountFromWorkingHours = 0;

      // Using imported transformDeductionToWorkingHour from deduction.utils.ts
      // This function correctly sets article numbers based on isTaxFreeArticle flag

      if (invoiceData.serviceTableDataByFreelancer) {
        for (const freelancerId of Object.keys(invoiceData.serviceTableDataByFreelancer)) {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const serviceWorkingHours = freelancerData?.workingHours || [];
          const freelancerDeductions = deductionsByFreelancer[freelancerId] || [];
          const deductionWorkingHours: any[] = [];
          const currentVat = preferences?.vat || 25;
          const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

          for (const deduction of freelancerDeductions) {
            try {
              const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
              deductionWorkingHours.push(deductionWH);
            } catch (error) {
              console.error('Error transforming deduction:', error, deduction);
            }
          }

          const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];
          const workingHoursWithUserId = allWorkingHours.map((wh: any) => {
            totalAmountFromWorkingHours += Number(wh.amount || 0);
            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
              userId: freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId,
            };
          });
          totalWorkingDates = [...totalWorkingDates, ...workingHoursWithUserId];
        }
      } else if (serviceData?.workingHours) {
        const serviceWorkingHours = serviceData.workingHours || [];
        const freelancerDeductions = deductionsByFreelancer['default'] || [];
        const deductionWorkingHours: any[] = [];
        const currentVat = preferences?.vat || 25;
        const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

        for (const deduction of freelancerDeductions) {
          try {
            const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
            deductionWorkingHours.push(deductionWH);
          } catch (error) {
            console.error('Error transforming deduction:', error, deduction);
          }
        }

        const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];
        totalWorkingDates = allWorkingHours.map((wh: any) => {
          totalAmountFromWorkingHours += Number(wh.amount || 0);
          return {
            ...wh,
            id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
            userId: userDetails?.userId || userDetails?.id,
          };
        });
      }

      // Add working hours from copiedInvoiceData that haven't been removed (non-service articles only)
      // Get removedCopiedWorkingHours from invoiceData (passed via dataRef)
      const removedSet = invoiceData.removedCopiedWorkingHours || new Set();
      if (copiedInvoiceData?.workingHours && Array.isArray(copiedInvoiceData.workingHours)) {
        const currentVat = preferences?.vat || 25; // Get current selected VAT
        const copiedNonServiceWorkingHours = copiedInvoiceData.workingHours
          .map((wh: any, index: number) => ({ wh, originalIndex: index }))
          .filter(({ wh, originalIndex }: any) => {
            // Only include non-service articles (not 92, 94, 95, 96, 97)
            const isServiceArticle = ['92', '94', '95', '96', '97'].includes(wh.articleNumber);
            // Check if this working hour hasn't been removed
            const isNotRemoved = !removedSet.has(originalIndex);
            return !isServiceArticle && isNotRemoved;
          })
          .map(({ wh }: any) => {
            totalAmountFromWorkingHours += Number(wh.amount || 0);
            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID from copied invoice
              vat: wh.vat != null ? wh.vat : currentVat, // Preserve original VAT if exists
            };
          });



        totalWorkingDates = [...totalWorkingDates, ...copiedNonServiceWorkingHours];
      }

      // Prepare invoice freelancer array
      const invoiceFreelancerArr: any[] = [];
      if (invoiceData.serviceTableDataByFreelancer) {
        // Create a Set to track unique freelancer IDs
        const addedFreelancerIds = new Set<string>();

        Object.keys(invoiceData.serviceTableDataByFreelancer).forEach((freelancerId) => {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const workingHours = freelancerData?.workingHours || [];

          // Calculate amount for this freelancer (sum of their working hours subtotals)
          // Amount will be 0 if no working hours exist
          const freelancerAmount = workingHours.reduce((sum: number, wh: any) => {
            return sum + Number(wh.amount || 0);
          }, 0);
          const pensionData = pensionDataByFreelancer[freelancerId] || pensionDataByFreelancer["default"] || {};
          const holidayData = holidayDataByFreelancer[freelancerId] || holidayDataByFreelancer["default"] || {};
          const actualUserId = freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId;

          // Convert to string to ensure consistent comparison
          const userIdStr = actualUserId?.toString();

          // Skip if this freelancer has already been added
          if (addedFreelancerIds.has(userIdStr)) {
            return;
          }

          // Add to set to track
          addedFreelancerIds.add(userIdStr);

          invoiceFreelancerArr.push({
            userId: actualUserId,
            freelancerId: actualUserId,
            amount: freelancerAmount,
            pension: pensionData?.pension || false,
            pensionAmount: pensionData?.pensionAmount || 0,
            holidaySavings: holidayData?.holidaySavings || false,
            holidaySavingsFee: holidayData?.holidaySavingsFee || 0,
          });
        });
      }

      const deductionType = preferences?.deductionType || "none";
      const isROT = deductionType === "rot";
      const isRUT = deductionType === "rut";

      // Determine multipleFreelancer based on invoiceFreelancerArr length
      const multipleFreelancer = invoiceFreelancerArr.length > 1;

      // Update VAT in all working hours to match current preference
      // BUT preserve original VAT if it exists (for tax-free articles and deductions)
      const currentVatValue = preferences?.vat || 25;
      const updatedWorkingHours = totalWorkingDates.map((wh: any) => {
        return {
          ...wh,
          vat: wh.vat != null ? wh.vat : currentVatValue, // Preserve original VAT if exists
        };
      });

      // Build the payload
      const previewPayload: CreateInvoicePayload = {
        approvalInvoiceId: 0,
        rootDeductionAmount: calculatedAmounts?.rootDeductionAmount || 0,
        rutDeductionAmount: calculatedAmounts?.rutDeductionAmount || 0,
        invoiceId: 0,
        sendInvoice: 1,
        vat: preferences?.vat || 25,
        pension: pensionToSend,
        amount: totalAmountFromWorkingHours || 0,
        isFixed: formData?.isFixed || false,
        currency: preferences?.currency || "SEK",
        brfNo: preferences?.brfOrgNumber || "",
        registerAssignment: false,
        showDates: preferences?.showDates === 'yes' || preferences?.showDates === true ? true : false,
        house: preferences?.accommodationType === "house",
        holidaySavings: holidaySavingsToSend,
        description: formData?.otherDetails?.customerNotes || formData?.description || "",
        taxValue: calculatedAmounts?.tax || 0,
        totalToPay: calculatedAmounts?.net || 0,
        rutDeduction: isRUT,
        rootDeduction: isROT,
        file: formData?.otherDetails?.supportingFiles?.[0] || null,
        expressPayout: formData?.expressPayout || false,
        workingHours: updatedWorkingHours,
        email: selectedCustomer.email || "",
        apartment: preferences?.accommodationType === "apartment" || (isROT && preferences?.accommodationType !== "house"),
        pensionFee: pensionToSend ? calculatedAmounts?.pensionPercentage || 0 : 0,
        holidaySavingsFee: holidaySavingsToSend ? holidaySavingsFeeToSend : 0,
        tax: userDetails?.incomeTax || 0,
        rotApartmentNo: preferences?.accommodationType === "house" ? (preferences?.property || "") : (preferences?.apartmentHouseNo || ""),
        pensionAmount: pensionAmountToSend,
        customerId: customerId,
        expenditureForMaterialsAndToolsTaxFreeAmount: 0,
        holidaySavingsAmount: calculatedAmounts?.holidayPayAmount || 0,
        amountIncVat: Math.round(calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat || calculatedAmounts?.amountIncludingVat || 0),
        secondaryEmailAddress: selectedCustomer.secondaryEmailAddress || "",
        multipleFreelancer: multipleFreelancer,
        currencyRate: globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || "1",
        invoiceFreelancer: invoiceFreelancerArr,
        locale: customerSelectedLanguage || "sv-se"
      };

      // Call preliminary invoice API
      const result = await dispatch(createPreliminaryInvoice(previewPayload)).unwrap();


      // Open preview modal after successful API call
      setShowPreviewModal(true);
    } catch (error: any) {
      console.error("❌ Create Preliminary Invoice Error:", error);
      // Error toast already shown in Redux reducer
    }
  };

  const handleSaveDraft = () => {

  };

  const handleBankIdDraftSaveClick = () => {
    const invoiceData = invoiceDataRef.current;
    // 1. Customer must be selected
    if (!invoiceData?.formData?.customer?.id) {
      toast.error(String(t('DRAFT_REQUIRES_CUSTOMER')));
      return;
    }

    // 2. At least one working hour row must exist
    const byFreelancer = invoiceData?.serviceTableDataByFreelancer;
    const hasWorkingHours = !!(byFreelancer && Object.keys(byFreelancer).length > 0 &&
      Object.values(byFreelancer).some(
        (fd: any) => Array.isArray(fd?.workingHours) && fd.workingHours.length > 0
      ));
    if (!hasWorkingHours) {
      toast.error(String(t('DRAFT_REQUIRES_WORKING_HOURS')));
      return;
    }

    // 3. Validate description
    const description = invoiceData?.formData?.otherDetails?.customerNotes || "";
    if (!description || description.trim().length === 0) {
      toast.error(String(t("PLEASE_ENTER_DESCRIPTION_BEFORE_CREATING_INVOICE") || "Please enter customer notes (description) before creating invoice"));
      return;
    }
    const descriptionLength = description.trim().length;
    const wordCount = description.trim().split(/\s+/).filter((word: string) => word.length > 0).length;
    if (descriptionLength < 20 && wordCount < 3) {
      toast.error(String(t("Enter at least 20 characters or 3 words") || "Enter at least 20 characters or 3 words in customer notes"));
      return;
    }

    // 4. Validate service descriptions for all freelancers
    if (validateServiceDescriptionsRef?.current) {
      const validationFunctions = validateServiceDescriptionsRef.current;
      const freelancerIds = Object.keys(validationFunctions);
      for (const freelancerId of freelancerIds) {
        const validateFunc = validationFunctions[freelancerId];
        if (validateFunc && !validateFunc()) {
          toast.error(String(t("PLEASE_FILL_SERVICE_DESCRIPTION") || "Please fill in service description for all working hours"));
          return;
        }
      }
    }

    setShowBankIdVerification(false);
    setShowBankIdDraftModal(true);
  };

  const handleSaveAndShare = () => {

  };

  // Function to build invoice payload for API calls
  const buildInvoicePayload = async () => {
    try {
      const invoiceData = invoiceDataRef.current;


      if (!invoiceData) {
        console.error("❌ No invoice data available");
        return null;
      }

      const selectedCustomer = customerState?.selectedCustomer;
      if (!selectedCustomer || (!selectedCustomer.id && !selectedCustomer.customerId)) {
        console.error("❌ No customer selected");
        return null;
      }

      const customerId = selectedCustomer.customerId || selectedCustomer.id;
      const { preferences = {}, serviceData = {}, calculatedAmounts = {}, formData = {}, pensionDataByFreelancer = {}, holidayDataByFreelancer = {}, deductionsByFreelancer = {}, selectedFreelancersForInvoice = [] } = invoiceData;

      // Collective = MORE than one freelancer (single mode keeps exactly 1: the
      // current user). Pension/holiday are then keyed by userId, not "default".
      const isCollectiveInvoice = selectedFreelancersForInvoice && selectedFreelancersForInvoice.length > 1;

      // Prepare pension data
      let pensionToSend = false;
      let pensionAmountToSend = 0;

      if (isCollectiveInvoice) {
        const allHavePension = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return pensionDataByFreelancer[freelancerId]?.pension;
        });
        pensionToSend = allHavePension;
        pensionAmountToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const amount = pensionDataByFreelancer[freelancerId]?.pensionAmount || 0;
          return sum + amount;
        }, 0);
      } else {
        const defaultPensionData = pensionDataByFreelancer["default"];
        pensionToSend = defaultPensionData?.pension || false;
        pensionAmountToSend = defaultPensionData?.pensionAmount || 0;
      }

      // Prepare holiday savings data
      let holidaySavingsToSend = false;
      let holidaySavingsFeeToSend = 0;

      if (isCollectiveInvoice) {
        const allHaveHolidaySavings = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return holidayDataByFreelancer[freelancerId]?.holidaySavings;
        });
        holidaySavingsToSend = allHaveHolidaySavings;
        holidaySavingsFeeToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const fee = holidayDataByFreelancer[freelancerId]?.holidaySavingsFee || 0;
          return sum + fee;
        }, 0);
      } else {
        const defaultHolidayData = holidayDataByFreelancer["default"];
        holidaySavingsToSend = defaultHolidayData?.holidaySavings || false;
        holidaySavingsFeeToSend = defaultHolidayData?.holidaySavingsFee || 0;
      }

      let totalWorkingDates: any[] = [];
      let totalAmountFromWorkingHours = 0;
      const invoiceFreelancerArr: any[] = [];

      // Using imported transformDeductionToWorkingHour from deduction.utils.ts
      // This function correctly sets article numbers based on isTaxFreeArticle flag

      // Process working hours from serviceTableDataByFreelancer or serviceData
      if (invoiceData.serviceTableDataByFreelancer) {
        for (const freelancerId of Object.keys(invoiceData.serviceTableDataByFreelancer)) {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const serviceWorkingHours = freelancerData?.workingHours || [];
          const freelancerDeductions = deductionsByFreelancer[freelancerId] || [];
          const deductionWorkingHours: any[] = [];
          const currentVat = preferences?.vat || 25;
          const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

          for (const deduction of freelancerDeductions) {
            try {
              const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
              deductionWorkingHours.push(deductionWH);
            } catch (error) {
              console.error('Error transforming deduction:', error, deduction);
            }
          }

          const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];
          const workingHoursWithUserId = allWorkingHours.map((wh: any) => {
            totalAmountFromWorkingHours += Number(wh.amount || 0);
            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
              userId: freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId,
            };
          });
          totalWorkingDates = [...totalWorkingDates, ...workingHoursWithUserId];
        }
      } else if (serviceData?.workingHours) {
        const serviceWorkingHours = serviceData.workingHours || [];
        const freelancerDeductions = deductionsByFreelancer['default'] || [];
        const deductionWorkingHours: any[] = [];
        const currentVat = preferences?.vat || 25;
        const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

        for (const deduction of freelancerDeductions) {
          try {
            const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
            deductionWorkingHours.push(deductionWH);
          } catch (error) {
            console.error('Error transforming deduction:', error, deduction);
          }
        }

        const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];
        totalWorkingDates = allWorkingHours.map((wh: any) => {
          totalAmountFromWorkingHours += Number(wh.amount || 0);
          return {
            ...wh,
            id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
            userId: userDetails?.userId || userDetails?.id,
          };
        });
      }

      // Add working hours from copiedInvoiceData that haven't been removed (non-service articles only)
      if (copiedInvoiceData?.workingHours && Array.isArray(copiedInvoiceData.workingHours)) {
        const currentVat = preferences?.vat || 25; // Get current selected VAT
        // Get removedCopiedWorkingHours from invoiceData (passed via dataRef) - FIX for Save Draft
        const removedSet = invoiceData.removedCopiedWorkingHours || new Set();
        const copiedNonServiceWorkingHours = copiedInvoiceData.workingHours
          .map((wh: any, index: number) => ({ wh, originalIndex: index }))
          .filter(({ wh, originalIndex }: any) => {
            // Only include non-service articles (not 92, 94, 95, 96, 97)
            const isServiceArticle = ['92', '94', '95', '96', '97'].includes(wh.articleNumber);
            // Check if this working hour hasn't been removed - USE removedSet from dataRef
            const isNotRemoved = !removedSet.has(originalIndex);
            return !isServiceArticle && isNotRemoved;
          })
          .map(({ wh }: any) => {
            totalAmountFromWorkingHours += Number(wh.amount || 0);
            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID from copied invoice
              vat: wh.vat != null ? wh.vat : currentVat, // Preserve original VAT if exists
            };
          });



        totalWorkingDates = [...totalWorkingDates, ...copiedNonServiceWorkingHours];
      }

      // Prepare invoice freelancer array
      if (invoiceData.serviceTableDataByFreelancer) {
        // Create a Set to track unique freelancer IDs
        const addedFreelancerIds = new Set<string>();

        Object.keys(invoiceData.serviceTableDataByFreelancer).forEach((freelancerId) => {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const workingHours = freelancerData?.workingHours || [];

          // Calculate amount for this freelancer (sum of their working hours subtotals)
          // Amount will be 0 if no working hours exist
          const freelancerAmount = workingHours.reduce((sum: number, wh: any) => {
            return sum + Number(wh.amount || 0);
          }, 0);
          const pensionData = pensionDataByFreelancer[freelancerId] || pensionDataByFreelancer["default"] || {};
          const holidayData = holidayDataByFreelancer[freelancerId] || holidayDataByFreelancer["default"] || {};
          const actualUserId = freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId;

          // Convert to string to ensure consistent comparison
          const userIdStr = actualUserId?.toString();

          // Skip if this freelancer has already been added
          if (addedFreelancerIds.has(userIdStr)) {
            return;
          }

          // Add to set to track
          addedFreelancerIds.add(userIdStr);

          invoiceFreelancerArr.push({
            userId: actualUserId,
            freelancerId: actualUserId,
            amount: freelancerAmount,
            pension: pensionData?.pension || false,
            pensionAmount: pensionData?.pensionAmount || 0,
            holidaySavings: holidayData?.holidaySavings || false,
            holidaySavingsFee: holidayData?.holidaySavingsFee || 0,
          });
        });
      }


      const deductionType = preferences?.deductionType || "none";
      const isROT = deductionType === "rot";
      const isRUT = deductionType === "rut";

      const multipleFreelancer = invoiceFreelancerArr.length > 1;

      // Update VAT in all working hours to match current preference
      // BUT preserve original VAT if it exists (for tax-free articles and deductions)
      const currentVatValue = preferences?.vat || 25;
      const updatedWorkingHours = totalWorkingDates.map((wh: any) => {
        return {
          ...wh,
          vat: wh.vat != null ? wh.vat : currentVatValue, // Preserve original VAT if exists
        };
      });



      const payload: CreateInvoicePayload = {
        approvalInvoiceId: 0,
        rootDeductionAmount: calculatedAmounts?.rootDeductionAmount || 0,
        rutDeductionAmount: calculatedAmounts?.rutDeductionAmount || 0,
        invoiceId: preId ? parseInt(preId, 10) : 0, // Use preId from URL if available
        sendInvoice: 1,
        vat: preferences?.vat || 25,
        pension: pensionToSend,
        amount: totalAmountFromWorkingHours || 0,
        isFixed: formData?.isFixed || false,
        currency: preferences?.currency || "SEK",
        brfNo: preferences?.brfOrgNumber || "",
        registerAssignment: false,
        showDates: preferences?.showDates === 'yes' || preferences?.showDates === true ? true : false,
        house: preferences?.accommodationType === "house",
        holidaySavings: holidaySavingsToSend,
        description: formData?.otherDetails?.customerNotes || formData?.description || "",
        taxValue: calculatedAmounts?.tax || 0,
        totalToPay: calculatedAmounts?.net || 0,
        rutDeduction: isRUT,
        rootDeduction: isROT,
        file: formData?.otherDetails?.supportingFiles?.length > 0
          ? formData.otherDetails.supportingFiles.map((f: any) => ({
            fileName: f.fileName,
            fileContent: f.fileContent
          }))
          : [],
        expressPayout: formData?.expressPayout || false,
        expressFee: (formData?.expressPayout || false) ? 5.9 : 0,
        workingHours: updatedWorkingHours,
        email: selectedCustomer.email || "",
        apartment: preferences?.accommodationType === "apartment" || (isROT && preferences?.accommodationType !== "house"),
        pensionFee: pensionToSend ? calculatedAmounts?.pensionPercentage || 0 : 0,
        holidaySavingsFee: holidaySavingsToSend ? holidaySavingsFeeToSend : 0,
        tax: userDetails?.incomeTax || 0,
        rotApartmentNo: preferences?.accommodationType === "house" ? (preferences?.property || "") : (preferences?.apartmentHouseNo || ""),
        pensionAmount: pensionAmountToSend,
        customerId: customerId,
        expenditureForMaterialsAndToolsTaxFreeAmount: 0,
        holidaySavingsAmount: calculatedAmounts?.holidayPayAmount || 0,
        amountIncVat: Math.round(calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat || calculatedAmounts?.amountIncludingVat || 0),
        secondaryEmailAddress: selectedCustomer.secondaryEmailAddress || "",
        multipleFreelancer: multipleFreelancer,
        currencyRate: globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || "1",
        invoiceFreelancer: invoiceFreelancerArr,
        locale: i18n.language || "en-us",
        createdDate: createdDateFromUrl || new Date().toISOString(),
        dueDate: preferences?.dueDate || "", // NEW - Due date from preferences
      };

      return payload;
    } catch (error) {
      console.error("❌ Error building invoice payload:", error);
      return null;
    }
  };

  // Function to handle second API call with PDF blob
  const handleSecondApiCall = async (pdfBlob: Blob): Promise<void> => {
    try {
      // Build the same payload as used in first API call
      const payload = await buildInvoicePayload();


      if (!payload) {
        toast.error("Unable to build invoice payload");
        throw new Error("Unable to build invoice payload");
      }

      // Convert PDF blob to base64
      const pdfBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove the data URL prefix (data:application/pdf;base64,)
          const base64 = base64String.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      // Create the final payload with PDF - same structure as /invoice/new-add-invoice
      const draftPayload: CreateDraftInvoicePayload = {
        ...payload,
        invoicePdf: pdfBase64, // Add PDF as base64 string
      };



      // Call the draft invoice API using Redux action
      const result = await dispatch(createDraftInvoice(draftPayload)).unwrap();


      // Navigate to draft invoice page after success
      const locale = i18n.language || 'en-us';
      window.location.href = `/v2/draft-invoice?lang=${locale}`;
    } catch (error) {
      console.error("❌ Second API Error:", error);
      throw error;
    }
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // ── Guided tour (Start Tour) — controlled: page owns run + step index ─────
  const [tourRun, setTourRun] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  // Frozen at tour start: whether saved customers existed. Keeps the customer
  // phase branch stable even after a customer is created mid-tour.
  const [tourStartedWithCustomers, setTourStartedWithCustomers] = useState(false);
  // True while the pension "gross salary ≥ 5000" error tooltip is on screen — the
  // Pension step then swaps its content to the error and spotlights the tooltip.
  const [tourPensionError, setTourPensionError] = useState(false);
  // True while the "See how we calculated" breakdown modal is open — the
  // calculation step then swaps its content + spotlights the modal.
  const [tourCalcModalOpen, setTourCalcModalOpen] = useState(false);
  // Server-driven welcome modal (offers to start the guided tour).
  const [showTourWelcome, setShowTourWelcome] = useState(false);
  // /useronboarding GET result → onBoardingSteps.IsCreateInvoiceTourSkip.
  //   null  — GET not resolved yet (don't show)
  //   false — first-time; SHOW the welcome modal
  //   true  — already dismissed; NEVER show
  const userOnboardingData = useAppSelector((state: any) => state?.auth?.userOnboardingData);
  const isCreateInvoiceTourSkip: boolean | null = React.useMemo(() => {
    if (!userOnboardingData) return null;
    const steps = parseOnboardingSteps(userOnboardingData?.onBoardingSteps);
    const flag = steps?.IsCreateInvoiceTourSkip;
    return typeof flag === 'boolean' ? flag : null;
  }, [userOnboardingData]);

  // ── BankID identity verification modal ───────────────────────────────────
  const [showBankIdVerification, setShowBankIdVerification] = useState(false);
  const [verificationMode, setVerificationMode] = useState<null | 'bankid' | 'document'>(null);
  const [showBankIdDraftModal, setShowBankIdDraftModal] = useState(false);
  const isIdentityVerified = useAppSelector((state: any) => state?.bankidAuthFlow?.isIdentityVerified);
  const canVerifyViaBankId = useAppSelector((state: any) => state?.bankidAuthFlow?.canVerifyViaBankId);
  const latestDocumentStatus = useAppSelector((state: any) => state?.bankidAuthFlow?.latestDocumentStatus);
  const verificationStatusLoading = useAppSelector((state: any) => state?.bankidAuthFlow?.verificationStatusLoading);
  const rejectionReason = useAppSelector((state: any) => state?.bankidAuthFlow?.rejectionReason);
  const bankidDocuments = useAppSelector((state: any) => state?.bankidAuthFlow?.documents);

  // Document upload state for identity verification
  const [verificationDocument, setVerificationDocument] = useState<File | null>(null);
  const [verificationDocumentPreview, setVerificationDocumentPreview] = useState<string | null>(null);
  const [verificationDocumentName, setVerificationDocumentName] = useState<string>('');
  const [verificationDocumentType, setVerificationDocumentType] = useState<string>('');
  const [verificationDocumentError, setVerificationDocumentError] = useState<string | null>(null);
  const [verificationDocumentSubmitting, setVerificationDocumentSubmitting] = useState<boolean>(false);
  const verificationDocInputRef = React.useRef<HTMLInputElement>(null);
  const [addressProofDocument, setAddressProofDocument] = useState<File | null>(null);
  const [addressProofDocumentPreview, setAddressProofDocumentPreview] = useState<string | null>(null);
  const [addressProofDocumentName, setAddressProofDocumentName] = useState<string>('');
  const [addressProofDocumentError, setAddressProofDocumentError] = useState<string | null>(null);
  const addressProofDocInputRef = React.useRef<HTMLInputElement>(null);

  const handleVerificationDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      toast.error('Only PDF, PNG, JPG files are allowed');
      return;
    }
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (file.size > maxSize) {
      setVerificationDocumentError(t('ID_DOCUMENT_SIZE_ERROR'));
      if (verificationDocInputRef.current) verificationDocInputRef.current.value = '';
      return;
    }
    setVerificationDocumentError(null);
    setVerificationDocument(file);
    setVerificationDocumentPreview(file.name);
  };

  const handleVerificationDocumentRemove = () => {
    setVerificationDocument(null);
    setVerificationDocumentPreview(null);
    setVerificationDocumentName('');
    setVerificationDocumentType('');
    setVerificationDocumentError(null);
    if (verificationDocInputRef.current) verificationDocInputRef.current.value = '';
  };

  const handleAddressProofDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      toast.error('Only PDF, PNG, JPG files are allowed');
      return;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setAddressProofDocumentError(t('ID_DOCUMENT_SIZE_ERROR'));
      if (addressProofDocInputRef.current) addressProofDocInputRef.current.value = '';
      return;
    }
    setAddressProofDocumentError(null);
    setAddressProofDocument(file);
    setAddressProofDocumentPreview(file.name);
  };

  const handleAddressProofDocumentRemove = () => {
    setAddressProofDocument(null);
    setAddressProofDocumentPreview(null);
    setAddressProofDocumentName('');
    setAddressProofDocumentError(null);
    if (addressProofDocInputRef.current) addressProofDocInputRef.current.value = '';
  };

  const handleVerificationDocumentSubmit = async () => {
    const idDocApprovedFromServer = bankidDocuments?.[0]?.status === 1;
    const addressDocApprovedFromServer = bankidDocuments?.[1]?.status === 1;

    // If ID doc not yet approved, it must be newly uploaded with a type selected
    if (!idDocApprovedFromServer && (!verificationDocument || !verificationDocumentType || (verificationDocumentType === 'other' && !verificationDocumentName.trim()))) return;
    // If address proof not yet approved, it must be newly uploaded with a name
    if (!addressDocApprovedFromServer && (!addressProofDocument || !addressProofDocumentName.trim())) return;

    try {
      setVerificationDocumentSubmitting(true);
      const formData = new FormData();

      // Only append ID doc if it wasn't already approved server-side
      if (!idDocApprovedFromServer && verificationDocument) {
        formData.append('files', verificationDocument, verificationDocument.name);
        const docName = verificationDocumentType !== 'other'
          ? t(`ID_DOCUMENT_TYPE_${verificationDocumentType.toUpperCase()}`)
          : verificationDocumentName.trim();
        formData.append('documentNames', docName);
      }

      // Only append address proof if it wasn't already approved server-side
      if (!addressDocApprovedFromServer && addressProofDocument) {
        formData.append('files', addressProofDocument, addressProofDocument.name);
        formData.append('documentNames', addressProofDocumentName.trim());
      }
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/bankid-verification/upload-documents`,
        {
          method: 'POST',
          // NO Content-Type header — browser sets multipart/form-data with correct boundary
          headers: { Authorization: `Bearer ${user?.token}` },
          body: formData,
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || 'Failed to upload document');
      }
      setShowBankIdVerification(false);
      setAddressProofDocument(null);
      setAddressProofDocumentPreview(null);
      setAddressProofDocumentName('');
      setAddressProofDocumentError(null);
      if (addressProofDocInputRef.current) addressProofDocInputRef.current.value = '';
      dispatch(fetchBankIdVerificationStatus() as any);
    } catch (err) {
      toast.error((err as any)?.message || 'Failed to upload document');
    } finally {
      setVerificationDocumentSubmitting(false);
    }
  };

  // Fetch verification status on page mount
  useEffect(() => {
    dispatch(fetchBankIdVerificationStatus() as any);
  }, []);

  // Open/close modal based on isIdentityVerified from /bankid-verification/status
  useEffect(() => {
    if (verificationStatusLoading) return;
    if (isIdentityVerified === true || latestDocumentStatus === LatestDocumentStatus.Approved) {
      setShowBankIdVerification(false);
    } else if (isIdentityVerified === false && latestDocumentStatus !== LatestDocumentStatus.Approved) {
      setShowBankIdVerification(true);
    }
  }, [isIdentityVerified, verificationStatusLoading, latestDocumentStatus]);

  const handleBankIdVerificationSuccess = (userData: any) => {
    const stored = localStorage.getItem('user');
    const parsed = stored ? JSON.parse(stored) : {};
    localStorage.setItem('user', JSON.stringify({ ...parsed, userData }));
    dispatch(updateUser(userData) as any);
    setShowBankIdVerification(false);
    setVerificationMode(null);
    setVerificationDocumentType('');
  };

  const handleBankIdVerificationClose = () => {
    setShowBankIdVerification(false);
    setVerificationMode(null);
    setVerificationDocumentType('');
  };

  // Auto-open document mode when BankID is not available (only one option)
  useEffect(() => {
    if (showBankIdVerification && canVerifyViaBankId === false && verificationMode === null) {
      setVerificationMode('document');
    }
  }, [showBankIdVerification, canVerifyViaBankId]);
  // ─────────────────────────────────────────────────────────────────────────

  // ── Unsaved-changes guard ─────────────────────────────────────────────────
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const allowedRef = useRef(false);

  // Returns true only when customer is selected AND at least one working-hour row exists
  const hasUnsavedWork = () => {
    // Never block navigation in edit mode
    if (isEditMode) return false;
    const ref = invoiceDataRef.current;
    // Show modal if ANY ONE of these is true:
    // 1. Customer is selected
    if (ref?.formData?.customer?.id) return true;
    // 2. Working hours > 0
    const byFreelancer = ref?.serviceTableDataByFreelancer;
    const hasWorkingHours = !!(byFreelancer && Object.keys(byFreelancer).length > 0 &&
      Object.values(byFreelancer).some(
        (fd: any) => Array.isArray(fd?.workingHours) && fd.workingHours.length > 0
      ));
    if (hasWorkingHours) return true;
    // 3. Description is filled
    if (ref?.formData?.otherDetails?.customerNotes?.trim()) return true;
    // Nothing filled → navigate freely
    return false;
  };

  // Block browser refresh / tab-close / external navigation
  // (removed — only URL change and version-switch trigger the modal)

  // Block in-app navigation (compatible with BrowserRouter — no data router needed)
  useEffect(() => {
    const originalPushState = window.history.pushState.bind(window.history);

    window.history.pushState = function (data: any, unused: string, url?: string | URL | null) {
      if (allowedRef.current) {
        return originalPushState(data, unused, url);
      }
      let targetPathname = window.location.pathname;
      if (url) {
        try { targetPathname = new URL(url.toString(), window.location.href).pathname; } catch {}
      }
      // Allow same-page changes (query param / hash updates)
      if (targetPathname === window.location.pathname) {
        return originalPushState(data, unused, url);
      }
      pendingNavigationRef.current = () => {
        allowedRef.current = true;
        originalPushState(data, unused, url);
        window.dispatchEvent(new PopStateEvent('popstate', { state: data }));
        allowedRef.current = false;
      };
      // Only block if customer is selected AND working hours exist
      if (!hasUnsavedWork()) {
        pendingNavigationRef.current();
        pendingNavigationRef.current = null;
        return;
      }
      setShowUnsavedModal(true);
    } as typeof window.history.pushState;

    const handlePopState = () => {
      if (allowedRef.current) return;
      // Only block if customer is selected AND working hours exist
      if (!hasUnsavedWork()) return;
      // Push current URL back so we stay on this page
      window.history.pushState(null, '', window.location.href);
      pendingNavigationRef.current = () => {
        allowedRef.current = true;
        window.history.go(-2);
        allowedRef.current = false;
      };
      setShowUnsavedModal(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.history.pushState = originalPushState;
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Allow navigation when invoice is successfully sent (bypass guard).
  // ALSO clear the customer/freelancer selection here — the invoice is done and
  // we're about to leave for Manage Invoice, so the NEXT time the user starts a
  // fresh invoice they don't inherit this invoice's selected freelancers. Doing
  // it now (before navigating away) avoids the mount-time race with the
  // Collaborators section, which otherwise re-writes the stale selection back
  // into the store before the page's fresh-creation reset can run.
  useEffect(() => {
    const handleInvoiceSent = () => {
      allowedRef.current = true;
      dispatch({ type: 'customer/resetCustomerSelection' });
    };
    window.addEventListener('invoice-sent-successfully', handleInvoiceSent);
    return () => window.removeEventListener('invoice-sent-successfully', handleInvoiceSent);
  }, [dispatch]);
  // ─────────────────────────────────────────────────────────────────────────

  const handleSendInvoice = async (): Promise<any> => {
    try {

      // Get invoice data from ref (will be set by CreateInvoice component)
      const invoiceData = invoiceDataRef.current;

      if (!invoiceData) {
        toast.error(String(t("PLEASE_FILL_ALL_REQUIRED_INVOICE_DETAILS") || "Please fill in all required invoice details."));
        console.error("❌ No invoice data available");
        return false;
      }

      // Require at least ONE working-hour line. Adding only deductions (with no
      // service row of any pricing type) is not a valid invoice. Applies to both
      // Send Invoice and Edit Request (both go through this handler).
      const svcByFreelancer = invoiceData?.serviceTableDataByFreelancer;
      let totalWorkingHours = 0;
      if (svcByFreelancer && Object.keys(svcByFreelancer).length > 0) {
        totalWorkingHours = Object.values(svcByFreelancer).reduce(
          (n: number, fd: any) => n + (Array.isArray(fd?.workingHours) ? fd.workingHours.length : 0),
          0
        );
      } else if (Array.isArray(invoiceData?.serviceData?.workingHours)) {
        totalWorkingHours = invoiceData.serviceData.workingHours.length;
      }
      if (totalWorkingHours === 0) {
        toast.error(String(t("PLEASE_ADD_AT_LEAST_ONE_WORKING_HOUR") || "Please add at least one working hour before creating the invoice."));
        console.error("❌ No working hours — only deductions present");
        return false;
      }

      // Validate service descriptions for all freelancers before proceeding
      if (validateServiceDescriptionsRef.current) {
        const validationFunctions = validateServiceDescriptionsRef.current;
        const freelancerIds = Object.keys(validationFunctions);

        for (const freelancerId of freelancerIds) {
          const validateFunc = validationFunctions[freelancerId];
          if (validateFunc && !validateFunc()) {
            toast.error(String(t("PLEASE_FILL_SERVICE_DESCRIPTION") || "Please fill in service description for all working hours"));
            console.error(`❌ Service description validation failed for freelancer: ${freelancerId}`);
            return false;
          }
        }
      }

      // Validate description (customer notes) before proceeding
      const description = invoiceData?.formData?.otherDetails?.customerNotes || invoiceData?.formData?.description || "";

      if (!description || description.trim().length === 0) {
        toast.error(String(t("PLEASE_ENTER_DESCRIPTION_BEFORE_CREATING_INVOICE") || "Please enter customer notes (description) before creating invoice"));
        console.error("❌ Description is required");
        return false;
      }

      // Validate description length and word count
      const descriptionLength = description.trim().length;
      const wordCount = description.trim().split(/\s+/).filter((word: string) => word.length > 0).length;

      if (descriptionLength < 20 && wordCount < 3) {
        toast.error(String(t("Enter at least 20 characters or 3 words") || "Enter at least 20 characters or 3 words in customer notes"));
        console.error("❌ Description too short");
        return false;
      }

      // Get customer details
      const selectedCustomer = customerState?.selectedCustomer;

      if (!selectedCustomer || (!selectedCustomer.id && !selectedCustomer.customerId)) {
        toast.error("Please select a customer");
        console.error("❌ No customer selected");
        return false;
      }

      // Use customerId or id (whichever is available)
      const customerId = selectedCustomer.customerId || selectedCustomer.id;

      // Extract data from invoice
      const { preferences = {}, serviceData = {}, calculatedAmounts = {}, formData = {}, pensionDataByFreelancer = {}, holidayDataByFreelancer = {}, deductionsByFreelancer = {}, selectedFreelancersForInvoice = [] } = invoiceData;

      // Check if it's a collective invoice
      // Collective = MORE than one freelancer (single mode keeps exactly 1: the
      // current user). Pension/holiday are then keyed by userId, not "default".
      const isCollectiveInvoice = selectedFreelancersForInvoice && selectedFreelancersForInvoice.length > 1;

      // Prepare pension data
      let pensionToSend = false;
      let pensionAmountToSend = 0;

      if (isCollectiveInvoice) {
        const allHavePension = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return pensionDataByFreelancer[freelancerId]?.pension;
        });
        pensionToSend = allHavePension;

        pensionAmountToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const amount = pensionDataByFreelancer[freelancerId]?.pensionAmount || 0;
          return sum + amount;
        }, 0);
      } else {
        const defaultPensionData = pensionDataByFreelancer["default"];
        pensionToSend = defaultPensionData?.pension || false;
        pensionAmountToSend = defaultPensionData?.pensionAmount || 0;
      }

      // Prepare holiday savings data
      let holidaySavingsToSend = false;
      let holidaySavingsFeeToSend = 0;

      if (isCollectiveInvoice) {
        const allHaveHolidaySavings = selectedFreelancersForInvoice.every((freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          return holidayDataByFreelancer[freelancerId]?.holidaySavings;
        });
        holidaySavingsToSend = allHaveHolidaySavings;

        holidaySavingsFeeToSend = selectedFreelancersForInvoice.reduce((sum: number, freelancer: any) => {
          const freelancerId = (freelancer.userId || freelancer.id).toString();
          const fee = holidayDataByFreelancer[freelancerId]?.holidaySavingsFee || 0;
          return sum + fee;
        }, 0);
      } else {
        const defaultHolidayData = holidayDataByFreelancer["default"];
        holidaySavingsToSend = defaultHolidayData?.holidaySavings || false;
        holidaySavingsFeeToSend = defaultHolidayData?.holidaySavingsFee || 0;
      }

      // Prepare working hours data from service table
      // Combine all working hours from all freelancers into one array with userId
      let totalWorkingDates: any[] = [];
      let totalAmountFromWorkingHours = 0; // NEW - Calculate total amount from all working hours


      // Using imported transformDeductionToWorkingHour from deduction.utils.ts
      // This function correctly sets article numbers based on isTaxFreeArticle flag

      if (invoiceData.serviceTableDataByFreelancer) {
        // For collective invoice or service table data grouped by freelancer
        for (const freelancerId of Object.keys(invoiceData.serviceTableDataByFreelancer)) {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const serviceWorkingHours = freelancerData?.workingHours || [];

          // Get deductions for this freelancer and transform to working hours
          const freelancerDeductions = deductionsByFreelancer[freelancerId] || [];
          const deductionWorkingHours: any[] = [];

          const currentVat = preferences?.vat || 25;
          const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

          for (const deduction of freelancerDeductions) {
            try {
              const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
              deductionWorkingHours.push(deductionWH);
            } catch (error) {
              console.error('Error transforming deduction:', error, deduction);
            }
          }

          // Merge service working hours with deduction working hours
          const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];

          // Add userId to each working hour entry and calculate total amount
          const workingHoursWithUserId = allWorkingHours.map((wh: any) => {
            // Sum up the amount (subtotal) from each working hour
            totalAmountFromWorkingHours += Number(wh.amount || 0);

            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
              userId: freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId,
            };
          });

          totalWorkingDates = [...totalWorkingDates, ...workingHoursWithUserId];
        }
      } else if (serviceData?.workingHours) {
        // Fallback: if working hours are directly in serviceData
        const serviceWorkingHours = serviceData.workingHours || [];

        // Get deductions for default freelancer
        const freelancerDeductions = deductionsByFreelancer['default'] || [];
        const deductionWorkingHours: any[] = [];

        const currentVat = preferences?.vat || 25;
        const currencyRate = (globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || '1').toString();

        for (const deduction of freelancerDeductions) {
          try {
            const deductionWH = await transformDeductionToWorkingHour(deduction, currentVat, currencyRate);
            deductionWorkingHours.push(deductionWH);
          } catch (error) {
            console.error('Error transforming deduction:', error, deduction);
          }
        }


        // Merge service working hours with deduction working hours
        const allWorkingHours = [...serviceWorkingHours, ...deductionWorkingHours];

        totalWorkingDates = allWorkingHours.map((wh: any) => {
          totalAmountFromWorkingHours += Number(wh.amount || 0);

          return {
            ...wh,
            id: wh.id || 0, // Keep existing ID for edited hours, 0 for new hours
            userId: userDetails?.userId || userDetails?.id,
          };
        });
      }

      // Add working hours from copiedInvoiceData that haven't been removed (non-service articles only)
      // Get removedCopiedWorkingHours from invoiceData (passed via dataRef)
      const removedSet = invoiceData.removedCopiedWorkingHours || new Set();
      if (copiedInvoiceData?.workingHours && Array.isArray(copiedInvoiceData.workingHours)) {
        const currentVat = preferences?.vat || 25; // Get current selected VAT
        const copiedNonServiceWorkingHours = copiedInvoiceData.workingHours
          .map((wh: any, index: number) => ({ wh, originalIndex: index }))
          .filter(({ wh, originalIndex }: any) => {
            // Only include non-service articles (not 92, 94, 95, 96, 97)
            const isServiceArticle = ['92', '94', '95', '96', '97'].includes(wh.articleNumber);
            // Check if this working hour hasn't been removed
            const isNotRemoved = !removedSet.has(originalIndex);
            return !isServiceArticle && isNotRemoved;
          })
          .map(({ wh }: any) => {
            totalAmountFromWorkingHours += Number(wh.amount || 0);
            return {
              ...wh,
              id: wh.id || 0, // Keep existing ID from copied invoice
              vat: wh.vat != null ? wh.vat : currentVat, // Preserve original VAT if exists
            };
          });

        totalWorkingDates = [...totalWorkingDates, ...copiedNonServiceWorkingHours];
      }



      // Prepare invoice freelancer array - for both single and collective invoices
      const invoiceFreelancerArr: any[] = [];

      if (invoiceData.serviceTableDataByFreelancer) {
        // Create a Set to track unique freelancer IDs
        const addedFreelancerIds = new Set<string>();

        // Iterate through each freelancer's service table data
        Object.keys(invoiceData.serviceTableDataByFreelancer).forEach((freelancerId) => {
          const freelancerData = invoiceData.serviceTableDataByFreelancer[freelancerId];
          const workingHours = freelancerData?.workingHours || [];

          // Calculate amount for this freelancer (sum of their working hours subtotals)
          // Amount will be 0 if no working hours exist
          const freelancerAmount = workingHours.reduce((sum: number, wh: any) => {
            return sum + Number(wh.amount || 0);
          }, 0);

          // Get pension data for this freelancer
          const pensionData = pensionDataByFreelancer[freelancerId] || pensionDataByFreelancer["default"] || {};
          const holidayData = holidayDataByFreelancer[freelancerId] || holidayDataByFreelancer["default"] || {};

          // Get actual userId (for 'default', use current user's ID)
          const actualUserId = freelancerId === "default" ? userDetails?.userId || userDetails?.id : freelancerId;

          // Convert to string to ensure consistent comparison
          const userIdStr = actualUserId?.toString();

          // Skip if this freelancer has already been added
          if (addedFreelancerIds.has(userIdStr)) {
            return;
          }

          // Add to set to track
          addedFreelancerIds.add(userIdStr);

          invoiceFreelancerArr.push({
            userId: actualUserId,
            freelancerId: actualUserId,
            amount: freelancerAmount,
            pension: pensionData?.pension || false,
            pensionAmount: pensionData?.pensionAmount || 0,
            holidaySavings: holidayData?.holidaySavings || false,
            holidaySavingsFee: holidayData?.holidaySavingsFee || 0,
          });
        });
      }


      // Get deduction type from preferences
      const deductionType = preferences?.deductionType || "none";
      const isROT = deductionType === "rot";
      const isRUT = deductionType === "rut";

      // Determine multipleFreelancer based on invoiceFreelancerArr length
      const multipleFreelancer = invoiceFreelancerArr.length > 1;

      // Update VAT in all working hours to match current preference
      // BUT preserve original VAT if it exists (for tax-free articles and deductions)
      const currentVatValue = preferences?.vat || 25;
      const updatedWorkingHours = totalWorkingDates.map((wh: any) => {
       
        return {
          ...wh,
          vat: wh.vat != null ? wh.vat : currentVatValue, // Preserve original VAT if exists, else use current preference
        };
      });

      // Validate working hours: service rows must not have amount = 0 or amountPerUnit = 0
      const SERVICE_ARTICLE_NUMBERS = ['92', '94', '95', '96', '97'];
      const invalidServiceRows = updatedWorkingHours.filter((wh: any) => {
        const isServiceArticle = SERVICE_ARTICLE_NUMBERS.includes(String(wh.articleNumber));
        if (!isServiceArticle) return false;
        return Number(wh.amount) === 0 || Number(wh.amountPerUnit) === 0;
      });

      if (invalidServiceRows.length > 0) {
        toast.error(String(t('WORKING_HOUR_AMOUNT_ZERO_ERROR') || 'One or more working hours have amount 0. Please enter a valid price before creating the invoice.'));
        console.error('❌ Invoice validation failed: working hours with amount=0 or amountPerUnit=0', invalidServiceRows);
        return false;
      }

      // Build the payload
      const payload: CreateInvoicePayload = {
        approvalInvoiceId: 0,
        rootDeductionAmount: calculatedAmounts?.rootDeductionAmount || 0,
        rutDeductionAmount: calculatedAmounts?.rutDeductionAmount || 0,
        invoiceId: 0,
        sendInvoice: 1,
        vat: preferences?.vat || 25,
        draftId: preId || undefined,  // Pass preId as draftId if exists
        isDraft: preId ? true : false,  // Set isDraft to true if preId exists, otherwise false
        pension: pensionToSend,
        amount: totalAmountFromWorkingHours || 0, // Use calculated total from all working hours subtotals
        isFixed: formData?.isFixed || false,
        currency: preferences?.currency || "SEK",
        brfNo: preferences?.brfOrgNumber || "",
        registerAssignment: false,
        showDates: preferences?.showDates === 'yes' || preferences?.showDates === true ? true : false,
        house: preferences?.accommodationType === "house",
        holidaySavings: holidaySavingsToSend,
        description: formData?.otherDetails?.customerNotes || formData?.description || "",
        taxValue: calculatedAmounts?.tax || 0,
        totalToPay: calculatedAmounts?.net || 0,
        rutDeduction: isRUT,
        rootDeduction: isROT,
        file: formData?.otherDetails?.supportingFiles?.length > 0
          ? formData.otherDetails.supportingFiles.map((f: any) => ({
            fileName: f.fileName,
            fileContent: f.fileContent
          }))
          : [],
        expressPayout: formData?.expressPayout || false,
        expressFee: (formData?.expressPayout || false) ? 5.9 : 0,
        workingHours: updatedWorkingHours,
        email: selectedCustomer.email || "",
        apartment: preferences?.accommodationType === "apartment" || (isROT && preferences?.accommodationType !== "house"),
        pensionFee: pensionToSend ? calculatedAmounts?.pensionPercentage || 0 : 0,
        holidaySavingsFee: holidaySavingsToSend ? holidaySavingsFeeToSend : 0,
        tax: userDetails?.incomeTax || 0,
        rotApartmentNo: preferences?.accommodationType === "house" ? (preferences?.property || "") : (preferences?.apartmentHouseNo || ""),
        pensionAmount: pensionAmountToSend,
        customerId: customerId, // Use the extracted customerId
        expenditureForMaterialsAndToolsTaxFreeAmount: 0,
        holidaySavingsAmount: calculatedAmounts?.holidayPayAmount || 0,
        amountIncVat: Math.round(calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat || calculatedAmounts?.amountIncludingVat || 0),
        secondaryEmailAddress: selectedCustomer.secondaryEmailAddress || "",
        multipleFreelancer: multipleFreelancer,
        currencyRate: globalData?.currencies?.find((item: any) => item.description === (preferences?.currency || "SEK"))?.globalValue || "1",
        invoiceFreelancer: invoiceFreelancerArr,
        locale: i18n.language || "en-us",
        dueDate: preferences?.dueDate || "", // NEW - Due date from preferences
      };

      // Dispatch the appropriate action based on edit mode
      let result;
      if (isEditMode) {
        // Extract invoice number from URL for edit mode
        const searchParams = new URLSearchParams(location.search);
        const invoiceNumber = searchParams.get('invoiceNumber');
        // Add invoice number to payload for edit mode
        const editPayload = {
          ...payload,
          invoiceNumber: invoiceNumber ? parseInt(invoiceNumber) : 0,
          dueDate: payload.dueDate || "", // Explicitly include dueDate
          createdDate: createdDateFromUrl || payload.createdDate || new Date().toISOString(), // Explicitly include createdDate
        };

        // Call the edit invoice API directly
        const response = await apiCall.post('/editinvoiceV2/invoice-edit-request-v2', editPayload);
        result = response.data;


        if (result.statusCode === 200 || result.statusCode === 201) {
          toast.success(String(t('INVOICE_EDITED_SUCCESSFULLY')));
        }
      } else {
        result = await dispatch(createInvoice(payload)).unwrap();

        // 🆕 Extract isAlreadySynced from API response for TradeLab tracking
        if (result && typeof result === 'object' && result?.response?.isAlreadySynced !== undefined) {
          setIsAlreadySynced(result.response.isAlreadySynced);
        }
      }

      // Return full result object so modal can extract isAlreadySynced
      return result;
    } catch (error: any) {
      console.error("❌ Create Invoice Error:", error);
      // Error toast is already shown in Redux reducer
      return false;
    }
  };

  // 🆕 Check if environment is production
 
  // const isProduction = process.env.NODE_ENV === 'production' || window.location.hostname === 'invozio.com' || window.location.hostname === 'www.invozio.com';

  // 🆕 Get userId for TradeLab tracking
  const userId = userDetails?.userId || userDetails?.id || '';

  // ── Guided tour steps (built incrementally) ──────────────────────────────
  const tourCustomerSelected = !!(customerState?.selectedCustomer &&
    (customerState.selectedCustomer.customerId || customerState.selectedCustomer.id));
  const tourHasSavedCustomers = Array.isArray(customerState?.customers) && customerState.customers.length > 0;
  // A real service line exists once the invoice has a calculated total.
  const tourItemsHasTotal = Number(calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat || 0) > 0;

  const tourSteps: TourStep[] = React.useMemo(() => {
    // Customer phase differs depending on whether saved customers existed WHEN THE
    // TOUR STARTED (frozen) — so creating a customer mid-tour doesn't switch branch.
    const customerPhase: TourStep[] = tourStartedWithCustomers
      ? [
          {
            // Prefer the open Add-Customer modal so the spotlight lands ON it
            // (not the dimmed button behind); fall back to the "New customer" btn.
            anchor: ['.add-customer-modal .modal-content', '.cust-new-btn'],
            title: t('IVTOUR_ADD_CUSTOMER_TITLE') as string,
            body: t('IVTOUR_ADD_CUSTOMER_BODY') as string,
            placement: 'right',
            padding: 6,
          },
          {
            // Spotlight the trigger AND the open options list (union), so every
            // customer option is highlighted when the dropdown is open.
            anchor: ['.cust-select', '.cust-select .dropdown-list'],
            union: true,
            title: t('IVTOUR_SELECT_CUSTOMER_TITLE') as string,
            body: t('IVTOUR_SELECT_CUSTOMER_BODY') as string,
            placement: 'right',
            padding: 6,
            gate: tourCustomerSelected,
            gateHint: t('IVTOUR_SELECT_CUSTOMER_GATE') as string,
          },
        ]
      : [
          {
            // No saved customers → point at "New customer", BLOCK Next until one
            // is created (anchor prefers the open Add-Customer modal), then flip
            // to the picker.
            anchor: [
              '.add-customer-modal .modal-content',
              tourCustomerSelected ? '.cust-select' : '.cust-new-btn',
            ],
            title: (tourCustomerSelected ? t('IVTOUR_CUSTOMER_SELECTED_TITLE') : t('IVTOUR_ADD_YOUR_CUSTOMER_TITLE')) as string,
            body: (tourCustomerSelected ? t('IVTOUR_CUSTOMER_SELECTED_BODY') : t('IVTOUR_ADD_YOUR_CUSTOMER_BODY')) as string,
            placement: 'right',
            padding: 6,
            gate: tourCustomerSelected,
            gateHint: t('IVTOUR_ADD_YOUR_CUSTOMER_GATE') as string,
          },
        ];

    return [
      // ID-verification step ONLY when the user still needs to verify (i.e. the
      // modal actually opens). Already verified → skip it entirely.
      ...(isIdentityVerified ? [] : [{
        anchor: '.bankid-auth-overlay.login-theme .bankid-auth-modal',
        skipIfAbsent: true,
        title: t('IVTOUR_VERIFY_TITLE') as string,
        body: t('IVTOUR_VERIFY_BODY') as string,
        placement: 'right' as const,
        padding: 6,
      }]),
      {
        anchor: '.iw-scroll-side',
        // The workflow stepper is hidden on mobile, so this step is skipped there
        // (anchor has zero size → treated as absent). Desktop shows it as before.
        skipIfAbsent: true,
        title: t('IVTOUR_WORKFLOW_TITLE') as string,
        body: t('IVTOUR_WORKFLOW_BODY') as string,
        placement: 'right',
        padding: 8,
      },
      {
        // ONE step for both quick-action cards — Your logo + Copy recent
        // invoices — spotlighted together (union) and explained at once.
        anchor: ['.iw-quick-logo', '[data-tour="tour-copy-last"]'],
        union: true,
        title: t('IVTOUR_LOGO_COPY_TITLE') as string,
        body: t('IVTOUR_LOGO_COPY_BODY') as string,
        placement: 'bottom',
        padding: 6,
      },
      ...customerPhase,
      // ── Phase 4 — Collective toggle + Collaborators ───────────────────────
      {
        anchor: '.iw-quick-collective',
        title: t('IVTOUR_COLLAB_TOGGLE_TITLE') as string,
        body: t('IVTOUR_COLLAB_TOGGLE_BODY') as string,
        placement: 'bottom',
        padding: 6,
        // Optional step — turning on Collective invoice is NOT required to
        // continue the tour, so Next stays enabled (no gate).
      },
      {
        // ONE step for the whole Collaborators card — searching/adding
        // collaborators AND inviting new freelancers — instead of a separate
        // step for each. Skipped when Collective invoice is off (the card is
        // hidden / zero-size), so the tour flows straight to the next step.
        anchor: ['.collab-outer'],
        skipIfAbsent: true,
        title: t('IVTOUR_COLLAB_TITLE') as string,
        body: t('IVTOUR_COLLAB_BODY') as string,
        placement: 'top',
        padding: 8,
      },
      // ── Phase 5 — Preferences (ONE step for the whole card) ───────────────
      // Instead of a separate step for Currency / VAT / Due Date / RUT-ROT /
      // Show dates, spotlight the entire Preferences card once and explain them
      // all together — fewer steps, same information.
      {
        anchor: ['.pref-outer', '.invoice-preferences-card'],
        title: t('IVTOUR_PREFERENCES_TITLE') as string,
        body: t('IVTOUR_PREFERENCES_BODY') as string,
        placement: 'top',
        padding: 8,
      },
      // ── Phase 6 — Items, deductions, pension & holiday (ONE step) ─────────
      // Spotlight the whole freelancer card once and explain everything inside
      // it — adding rows/services, deductions, pension and holiday pay — instead
      // of a separate step for each. Fewer steps, same information.
      {
        // Collective view = .fc-user-section; single view = .user-section.
        anchor: ['.fc-user-section', '.user-section'],
        title: t('IVTOUR_ITEMS_TITLE') as string,
        body: t('IVTOUR_ITEMS_BODY') as string,
        placement: 'top',
        padding: 8,
        gate: tourItemsHasTotal,
        gateHint: t('IVTOUR_ITEMS_GATE') as string,
      },
      // ── Phase 7 — Payment & Summary (ONE step) · Other Details · Footer ───
      // Spotlight the whole Payment & Summary section once — Express Payment,
      // the totals, and the breakdown link — instead of a separate step each.
      {
        anchor: ['.iw-step5-parent'],
        skipIfAbsent: true,
        title: t('IVTOUR_PAYMENT_TITLE') as string,
        body: t('IVTOUR_PAYMENT_BODY') as string,
        placement: 'top',
        padding: 8,
      },
      {
        // ONE step for the whole Other Details card — Description + supporting file.
        anchor: ['.other-details'],
        skipIfAbsent: true,
        title: t('IVTOUR_OTHER_DETAILS_TITLE') as string,
        body: t('IVTOUR_OTHER_DETAILS_BODY') as string,
        placement: 'top',
        padding: 8,
      },
      {
        // ONE step for the whole footer action bar — Preview, Save as draft
        // and Send Invoice — instead of a separate step for each.
        anchor: ['.invoice-footer'],
        title: t('IVTOUR_FOOTER_TITLE') as string,
        body: t('IVTOUR_FOOTER_BODY') as string,
        placement: 'top',
        padding: 6,
      },
      {
        // Celebratory finish — centered card + confetti.
        placement: 'center',
        celebrate: true,
        title: t('IVTOUR_FINISH_TITLE') as string,
        body: t('IVTOUR_FINISH_BODY') as string,
      },
    ];
  }, [t, i18n.language, tourCustomerSelected, tourStartedWithCustomers, tourItemsHasTotal, isIdentityVerified, tourPensionError, tourCalcModalOpen]);

  const startTour = () => {
    setTourStartedWithCustomers(tourHasSavedCustomers);   // freeze the customer-phase branch
    // Only open the ID-verification modal (and show its step) when the user still
    // needs to verify; otherwise the tour begins at the Workflow step.
    if (!isIdentityVerified) {
      setShowBankIdVerification(true);
      setVerificationMode(null);
      setTourStep(0);
    } else {
      // Skip any leading steps whose anchor isn't present (e.g. the Workflow
      // stepper is hidden on mobile) so the tour opens on the first real step.
      let first = 0;
      while (
        first < tourSteps.length &&
        tourSteps[first]?.skipIfAbsent &&
        !tourAnchorPresent(tourSteps[first])
      ) first++;
      setTourStep(first);
    }
    setTourRun(true);
  };
  const endTour = () => {
    setShowBankIdVerification(false);
    setTourRun(false);
  };

  // ── Server-driven welcome modal (/useronboarding) ─────────────────────────
  // Fetch onboarding flags on mount so the decision uses the latest server state.
  useEffect(() => {
    dispatch(getUserOnboarding());
  }, [dispatch]);

  // Show ONLY when the server flag is explicitly false (first-time) and not in
  // edit mode. A small delay lets the page settle so the modal glides in.
  useEffect(() => {
    if (editMode || isCreateInvoiceTourSkip !== false) {
      setShowTourWelcome(false);
      return;
    }
    const id = window.setTimeout(() => setShowTourWelcome(true), 650);
    return () => window.clearTimeout(id);
  }, [isCreateInvoiceTourSkip, editMode]);

  // Persist the skip flag (PUT) then re-fetch (GET) so future visits stay hidden.
  const persistTourSkip = () => {
    updateUserOnboarding('IsCreateInvoiceTourSkip', true)
      .then(() => dispatch(getUserOnboarding()))
      .catch(() => { /* silent — non-blocking */ });
  };
  const handleWelcomeStart = () => {
    setShowTourWelcome(false);
    persistTourSkip();
    startTour();
  };
  const handleWelcomeIgnore = () => {
    setShowTourWelcome(false);
    persistTourSkip();
  };
  // Is a step's anchor currently visible? (centered steps count as present)
  // Zero-size elements (e.g. an empty Express-Payment column for non-Sweden
  // customers) count as absent so `skipIfAbsent` steps are skipped.
  const tourAnchorPresent = (step?: TourStep): boolean => {
    if (!step || !step.anchor) return true;
    const list = Array.isArray(step.anchor) ? step.anchor : [step.anchor];
    return list.some((sel) => {
      const el = document.querySelector(sel) as HTMLElement | null;
      return !!el && el.offsetWidth > 0 && el.offsetHeight > 0;
    });
  };
  // Modals that a step can spotlight (anchor → its own close button). When we
  // leave such a step, close the open modal via its own button (UI-only — the
  // modal's onHide/state handles the rest, so no data flow is touched).
  const TOUR_STEP_MODALS: { content: string; close: string }[] = [
    { content: '.add-customer-modal .modal-content', close: '.add-customer-modal .close-btn' },
    { content: '.copy-recent-invoice-modal .modal-content', close: '.copy-recent-invoice-modal .btn-close' },
    { content: '.invite-friends-modal .modal-content', close: '.invite-friends-modal .invite-modal-close' },
    { content: '.add-deduction-modal .modal-content', close: '.add-deduction-modal .close-btn' },
    { content: '.calculation-breakdown-modal .modal-content', close: '.calculation-breakdown-modal .btn-close' },
    { content: '.express-payment-modal .modal-content', close: '.express-payment-modal .btn-close' },
    { content: '.currency-info-modal .modal-content', close: '.currency-info-modal [aria-label="Close"]' },
    { content: '.vat-info-modal .modal-content', close: '.vat-info-modal [aria-label="Close"]' },
    { content: '.rut-rot-info-modal .modal-content', close: '.rut-rot-info-modal [aria-label="Close"]' },
  ];
  const closeTourModalFor = (step?: TourStep): void => {
    if (!step) return;
    const anchors = Array.isArray(step.anchor) ? step.anchor : [step.anchor];
    TOUR_STEP_MODALS.forEach((mod) => {
      if (anchors.includes(mod.content) && document.querySelector(mod.content)) {
        (document.querySelector(mod.close) as HTMLElement | null)?.click();
      }
    });
  };
  const tourPrev = () => {
    // If the current step spotlights an open modal, close it before stepping back.
    closeTourModalFor(tourSteps[tourStep]);
    let prev = tourStep - 1;
    while (prev > 0 && tourSteps[prev]?.skipIfAbsent && !tourAnchorPresent(tourSteps[prev])) prev--;
    setTourStep(Math.max(0, prev));
  };
  const tourNext = () => {
    // Leaving the ID-verification step → close that modal.
    const cur = tourSteps[tourStep];
    const curAnchors = cur ? (Array.isArray(cur.anchor) ? cur.anchor : [cur.anchor]) : [];
    if (curAnchors.some((a) => typeof a === 'string' && a.includes('bankid-auth'))) {
      setShowBankIdVerification(false);
    }
    // Any other spotlighted modal open on this step → close it too.
    closeTourModalFor(cur);
    let next = tourStep + 1;
    // Skip conditional steps whose anchor isn't present (e.g. pension hidden).
    while (next < tourSteps.length && tourSteps[next]?.skipIfAbsent && !tourAnchorPresent(tourSteps[next])) next++;
    if (next >= tourSteps.length) {
      setTourRun(false);   // last step → finish the tour
      return;
    }
    setTourStep(next);
  };

  // Whether there's a reachable (present) step BEFORE the current one. When the
  // Workflow step is hidden (mobile), the first visible step has no valid step to
  // go back to, so the Back button is hidden there — the user can't jump onto the
  // hidden first step. Desktop keeps Back exactly as before.
  const tourCanPrev = ((): boolean => {
    let p = tourStep - 1;
    while (p >= 0 && tourSteps[p]?.skipIfAbsent && !tourAnchorPresent(tourSteps[p])) p--;
    return p >= 0;
  })();

  // On the "Edit customer" step: once the user opens the Edit modal and then
  // closes it (after updating, or cancelling), auto-advance to the next step.
  const tourEditModalSeenRef = React.useRef(false);
  React.useEffect(() => {
    if (!tourRun) return;
    const cur = tourSteps[tourStep];
    const anchors = cur ? (Array.isArray(cur.anchor) ? cur.anchor : [cur.anchor]) : [];
    const isEditStep = anchors.some((a) => typeof a === 'string' && a.includes('cust-edit-btn'));
    tourEditModalSeenRef.current = false;
    if (!isEditStep) return;
    const id = window.setInterval(() => {
      const open = !!document.querySelector('.add-customer-modal .modal-content');
      if (open) {
        tourEditModalSeenRef.current = true;
      } else if (tourEditModalSeenRef.current) {
        tourEditModalSeenRef.current = false;
        tourNext();
      }
    }, 300);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRun, tourStep, tourSteps]);

  // On the Pension step: mirror the "gross salary ≥ 5000" error tooltip's
  // visibility into state so the step can swap its content + spotlight it.
  React.useEffect(() => {
    if (!tourRun) { setTourPensionError(false); return; }
    const cur = tourSteps[tourStep];
    const anchors = cur ? (Array.isArray(cur.anchor) ? cur.anchor : [cur.anchor]) : [];
    const isPensionStep = anchors.some((a) => typeof a === 'string' && a.includes('tour-pension'));
    if (!isPensionStep) { setTourPensionError(false); return; }
    const id = window.setInterval(() => {
      setTourPensionError(!!document.querySelector('[id^="pension-error-popover"]'));
    }, 250);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRun, tourStep, tourSteps]);

  // On the "See how we calculate" step: mirror the breakdown modal's visibility
  // into state so the step can swap its content + spotlight it.
  React.useEffect(() => {
    if (!tourRun) { setTourCalcModalOpen(false); return; }
    const cur = tourSteps[tourStep];
    const anchors = cur ? (Array.isArray(cur.anchor) ? cur.anchor : [cur.anchor]) : [];
    const isCalcStep = anchors.some((a) => typeof a === 'string' && a.includes('calculation-breakdown-modal'));
    if (!isCalcStep) { setTourCalcModalOpen(false); return; }
    const id = window.setInterval(() => {
      setTourCalcModalOpen(!!document.querySelector('.calculation-breakdown-modal .modal-content'));
    }, 250);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRun, tourStep, tourSteps]);

  return (
    <>
      {/* SEO: Canonical URL */}
      <Helmet>
        <link
          rel="canonical"
          href={i18n.language === LOCALES.EN_US ? "https://invozio.com/v2/create-invoice" : "https://invozio.com/v2/create-invoice"}
        />
      </Helmet>

      {/* 🆕 TradeLab Conversion Tracking - Fire only on production & first-time invoice */}
      {!isAlreadySynced && userId && (() => {
       
        return (
          <Helmet>
            <script type="text/javascript">
              {` 
                if (typeof tdconv !== "undefined" && tdconv !== null) {
                  tdconv('init', '2358708', {'element': 'iframe'});
                  tdconv('track', 'sale', {
                    'transactionId': '${userId}',
                    'ordervalue': '0',
                    'currency': 'SEK',
                    'event': 437992
                  });
                 
              `}
            </script>
          </Helmet>
        );
      })()}

      <div className="dashboard-page">
        <div className="dashboard-layout">
          <Sidebar activeItemId="create-invoices" footer={null}
            onClose={() => setIsSidebarOpen(false)}
            className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
          />
          {isSidebarOpen && (
            <div
              className="sidebar-overlay"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}
          {/* {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />} */}

          <div className="dashboard-main-content create-invoice-shell">
            <Header
              setIsSidebarOpen={setIsSidebarOpen}
              isCreateInvoicePage={true}
              onVersionSwitchRequest={(proceed) => {
                if (!hasUnsavedWork()) {
                  proceed();
                  return;
                }
                pendingNavigationRef.current = proceed;
                setShowUnsavedModal(true);
              }}
            />
            <div className="create-invoice-page main-content-wrapper" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
              {/* <Header /> */}

              <div className="">
                 <InvoiceNavigation
                      onStartTour={startTour}
                      showStartTour={isFreshInvoice}
                      onPreview={handlePreview}
                      onSaveDraft={handleSaveDraft}
                      onSaveAndShare={handleSaveAndShare}
                      isPreviousInvoicingTool={isPreviousInvoicingTool}
                      onTogglePreviousInvoicingTool={setIsPreviousInvoicingTool}
                      isEditMode={editMode}
                      isPreviewLoading={preliminaryLoading}
                      getPayload={buildInvoicePayload}
                      onSecondApiCall={handleSecondApiCall}
                      validateServiceDescriptionsRef={validateServiceDescriptionsRef}
                      dataRef={invoiceDataRef}
                    />
                <div className="row">
                  <div className="col-12">
                   

                    {/* Keep CreateInvoice mounted to preserve data */}
                    <div style={{ display: showPreviewModal ? 'none' : 'block' }}>
                      <CreateInvoice
                        dataRef={invoiceDataRef}
                        // Use the reliable edit flag (URL-derived, available on the
                        // first render) instead of the effect-set state, so edit-mode
                        // behaviour — e.g. preserving the fetched Express Payment —
                        // applies immediately and doesn't race the state update.
                        isEditMode={editMode}
                        preId={preId}
                        validateServiceDescriptionsRef={validateServiceDescriptionsRef}
                        validateDatesRef={validateDatesRef}
                        timesheetData={timesheetData}
                        invoiceNumber={invoiceNumberFromUrl}
                        backHandlerRef={wizardBackRef}
                        onStepChange={(step, total, title, subtitle) => {
                          setWizardStep(step);
                          setWizardTotalSteps(total);
                          setActiveStepTitle(title || '');
                          setActiveStepSubtitle(subtitle || '');
                        }}
                        onCalculatedSubAmountChange={(data) => {
                          setCalculatedSubAmount(data);
                        }}
                        onDeductionsByFreelancerChange={(data) => {
                          setDeductionsByFreelancer(data);
                        }}
                        onCopiedInvoiceDataChange={(data) => {
                          setCopiedInvoiceData(data);
                        }}
                        onPreferencesChange={(preferences) => {
                          // Update current due date when preferences change
                          if (preferences?.dueDate !== currentDueDate) {
                            setCurrentDueDate(preferences.dueDate || null);
                          }
                        }}
                      />

                    </div>

                    {/* Show modal on top */}
                    {showPreviewModal && (
                      <InvoicePreviewModal
                        isOpen={showPreviewModal}
                        onClose={() => setShowPreviewModal(false)}
                        brandName="Your Company"
                        logoUrl=""
                        invoiceNumber="INV-001"
                        totalAmount="SEK 10,000.00"
                        calculatedSubAmount={calculatedSubAmount}
                        isEditMode={isEditMode}
                        onSendInvoice={handleSendInvoice}
                        isLoading={createLoading}
                        invoiceDay={invoiceDataRef.current?.formData?.invoiceDay || ''}
                        dueDay={invoiceDataRef.current?.formData?.dueDay || ''}
                        deductionsByFreelancer={deductionsByFreelancer}
                        copiedInvoiceData={copiedInvoiceData}
                        onBankIdVerificationRequired={() => setShowBankIdVerification(true)}
                        dataRef={invoiceDataRef}
                        validateDatesRef={validateDatesRef}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Floating Help Button */}
              {/* <div className="floating-help-button">
              <button
                style={{
                  backgroundColor: "#7c3aed",
                  color: "white",
                  border: "none",
                  borderRadius: "24px",
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  boxShadow: "0px 4px 12px rgba(103, 80, 184, 0.3)",
                }}
              >
                <div
                  style={{
                    width: "30px",
                    height: "32px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.31814 11.6819 1.33334 8 1.33334C4.31814 1.33334 1.33334 4.31814 1.33334 8C1.33334 11.6819 4.31814 14.6667 8 14.6667Z" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M6.06666 6C6.21993 5.47362 6.56283 5.01615 7.03351 4.7071C7.50419 4.39805 8.07512 4.24885 8.64446 4.28323C9.21379 4.31761 9.75757 4.5334 10.1816 4.89229C10.6056 5.25119 10.8819 5.73073 10.9667 6.25334C10.9667 8 7.99999 9.16667 7.99999 9.16667" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8 11.5H8.00667" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                {t('NEED_HELP')}
              </button>
            </div> */}

              {/* Single-page scroll layout: the action footer is always available */}
              {true && (
              <InvoiceFooter
                grandTotal="XXXX Kr"
                onSendInvoice={handleSendInvoice}
                isLoading={createLoading}
                isEditMode={editMode}
                stepContextTitle={activeStepTitle}
                stepContextSubtitle={activeStepSubtitle}
                calculatedSubAmount={calculatedSubAmount}
                selectedCustomer={customerState?.selectedCustomer}
                validateServiceDescriptionsRef={validateServiceDescriptionsRef}
                validateDatesRef={validateDatesRef}
                dataRef={invoiceDataRef}
                  onPreview={handlePreview}
                onSaveDraft={handleSaveDraft}
                isPreviewLoading={preliminaryLoading}
                isPreviewOpen={showPreviewModal}
                onCancelPreview={() => setShowPreviewModal(false)}
                getPayload={buildInvoicePayload}
                onSecondApiCall={handleSecondApiCall}
                dueDateFromPreferences={currentDueDate || invoiceDataRef.current?.preferences?.dueDate || ''}
                onBankIdVerificationRequired={() => setShowBankIdVerification(true)}
                showBack={false}
                onBack={() => wizardBackRef.current?.()}
                className="invoice-footer--wizard"
              />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save Draft modal triggered from inside BankID verification modal */}
      <SaveInvoiceModal
        isOpen={showBankIdDraftModal}
        onClose={() => { setShowBankIdDraftModal(false); setShowBankIdVerification(true); }}
        onSave={() => {}}
        getPayload={buildInvoicePayload}
        onSecondApiCall={handleSecondApiCall}
      />

      {/* BankID identity verification modal */}
      {showBankIdVerification && (
        <BankIDAuth
          mode="login"
          collectMode="verification"
          onSuccess={handleBankIdVerificationSuccess}
          onClose={handleBankIdVerificationClose}
          className="login-theme"
          title={t('ID_VERIFICATION')}
          hideOptions={canVerifyViaBankId === false || latestDocumentStatus === LatestDocumentStatus.Pending || verificationMode !== 'bankid'}
          subHeaderContent={
            (verificationMode === 'bankid' || (verificationMode === 'document' && canVerifyViaBankId !== false)) ? (
              <button
                type="button"
                onClick={() => {
                  if (verificationMode === 'document') {
                    setVerificationDocumentType('');
                    setVerificationDocument(null);
                    setVerificationDocumentPreview(null);
                    setVerificationDocumentName('');
                    setVerificationDocumentError(null);
                    if (verificationDocInputRef.current) verificationDocInputRef.current.value = '';
                    setAddressProofDocument(null);
                    setAddressProofDocumentPreview(null);
                    setAddressProofDocumentName('');
                    setAddressProofDocumentError(null);
                    if (addressProofDocInputRef.current) addressProofDocInputRef.current.value = '';
                  }
                  setVerificationMode(null);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  border: '1.5px solid #e8e2f8',
                  background: '#f5f0ff',
                  cursor: 'pointer',
                  marginTop: '12px',
                  marginBottom: '12px',
                  transition: 'background 0.15s, border-color 0.15s',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#ede8fc'; e.currentTarget.style.borderColor = '#7c3aed'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#f5f0ff'; e.currentTarget.style.borderColor = '#e8e2f8'; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ) : undefined
          }
          footerContent={
            <div className="bankid-options" style={{ marginTop: '0' }}>
              {latestDocumentStatus === LatestDocumentStatus.Pending ? (
                /* ── Pending state ── */
                <div style={{ textAlign: 'center', padding: '20px 8px 12px' }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '64px', height: '64px', borderRadius: '50%',
                    background: 'linear-gradient(135deg,#fff8e1,#fef3c7)',
                    border: '2px solid #fde68a', marginBottom: '14px',
                    boxShadow: '0 4px 16px rgba(251,191,36,0.25)',
                  }}>
                    <span style={{ fontSize: '28px', lineHeight: 1 }}>⏳</span>
                  </div>
                  <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700, color: '#374151' }}>
                    {t('ID_DOCUMENT_PENDING_TITLE')}
                  </h3>
                  <p style={{ margin: 0, fontSize: '13.5px', color: '#6B7280', lineHeight: 1.6 }}>
                    {t('ID_DOCUMENT_PENDING_MSG')}
                  </p>
                  <p style={{ margin: '10px 0 0', fontSize: '12.5px', color: '#9ca3af', lineHeight: 1.5 }}>
                    {t('ID_DOCUMENT_PENDING_24H')}
                  </p>
                  <div style={{
                    marginTop: '20px', paddingTop: '16px',
                    borderTop: '1px solid #ede9fb',
                  }}>
                    <p style={{ fontSize: '13px', color: '#8b7cc8', margin: '0 0 12px', lineHeight: 1.55, fontWeight: 500 }}>
                      {t('SAVE_DRAFT_WHILE_WAITING')}
                    </p>
                    <button
                      type="button"
                      onClick={handleBankIdDraftSaveClick}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1.5px solid #7c3aed',
                        background: '#fff',
                        color: '#7c3aed',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16 16" fill="none">
                        <path d="M13.1893 2.06395L14.0803 1.60996L14.0803 1.60996L13.1893 2.06395ZM11.2013 1.33594L11.2013 2.33594L11.2013 2.33594L11.2013 1.33594ZM12.6066 1.48126L13.0606 0.590257L13.0606 0.590256L12.6066 1.48126ZM12.6066 14.5239L13.0606 15.415L13.0606 15.415L12.6066 14.5239ZM13.1893 13.9413L14.0803 14.3952L14.0803 14.3952L13.1893 13.9413ZM3.39598 14.5239L2.94199 15.415L2.94199 15.415L3.39598 14.5239ZM2.81329 13.9413L3.7043 13.4873L3.7043 13.4873L2.81329 13.9413ZM3.0585 5.61205L2.35139 4.90494L2.35139 4.90494L3.0585 5.61205ZM6.94411 1.72646L6.23701 1.01935L6.23701 1.01935L6.94411 1.72646ZM4.8013 14.6693V15.6693H11.2013V14.6693V13.6693H4.8013V14.6693ZM13.3346 12.5359H14.3346V3.46927H13.3346H12.3346V12.5359H13.3346ZM13.3346 3.46927H14.3346C14.3346 3.1124 14.3354 2.78461 14.3131 2.51211C14.2899 2.22793 14.2368 1.91704 14.0803 1.60996L13.1893 2.06395L12.2983 2.51794C12.2872 2.49608 12.3067 2.51448 12.3198 2.67497C12.3339 2.84714 12.3346 3.0794 12.3346 3.46927H13.3346ZM11.2013 1.33594L11.2013 2.33594C11.5912 2.33594 11.8234 2.33672 11.9956 2.35078C12.1561 2.36389 12.1745 2.38341 12.1526 2.37227L12.6066 1.48126L13.0606 0.590256C12.7535 0.433793 12.4426 0.380642 12.1585 0.357424C11.886 0.33516 11.5582 0.335937 11.2013 0.335938L11.2013 1.33594ZM13.1893 2.06395L14.0803 1.60996C13.8566 1.17091 13.4997 0.81396 13.0606 0.590257L12.6066 1.48126L12.1526 2.37227C12.2154 2.40423 12.2663 2.45522 12.2983 2.51794L13.1893 2.06395ZM11.2013 14.6693V15.6693C11.5582 15.6693 11.886 15.67 12.1585 15.6478C12.4426 15.6246 12.7535 15.5714 13.0606 15.415L12.6066 14.5239L12.1526 13.6329C12.1745 13.6218 12.1561 13.6413 11.9956 13.6544C11.8234 13.6685 11.5912 13.6693 11.2013 13.6693V14.6693ZM13.3346 12.5359H12.3346C12.3346 12.9258 12.3339 13.1581 12.3198 13.3302C12.3067 13.4907 12.2872 13.5091 12.2983 13.4873L13.1893 13.9413L14.0803 14.3952C14.2368 14.0882 14.2899 13.7773 14.3131 13.4931C14.3354 13.2206 14.3346 12.8928 14.3346 12.5359H13.3346ZM12.6066 14.5239L13.0606 15.415C13.4997 15.1912 13.8566 14.8343 14.0803 14.3952L13.1893 13.9413L12.2983 13.4873C12.2663 13.55 12.2154 13.601 12.1526 13.6329L12.6066 14.5239ZM4.8013 14.6693V13.6693C4.41144 13.6693 4.17918 13.6685 4.00701 13.6544C3.8465 13.6413 3.82811 13.6218 3.84997 13.6329L3.39598 14.5239L2.94199 15.415C3.24907 15.5714 3.55997 15.6246 3.84415 15.6478C4.11665 15.67 4.44444 15.6693 4.8013 15.6693V14.6693ZM2.66797 12.5359H1.66797C1.66797 12.8928 1.66719 13.2206 1.68946 13.4931C1.71267 13.7773 1.76582 14.0882 1.92229 14.3952L2.81329 13.9413L3.7043 13.4873C3.71544 13.5091 3.69593 13.4907 3.68281 13.3302C3.66875 13.1581 3.66797 12.9258 3.66797 12.5359H2.66797ZM3.39598 14.5239L3.84997 13.6329C3.78725 13.601 3.73626 13.55 3.7043 13.4873L2.81329 13.9413L1.92229 14.3952C2.14599 14.8343 2.50295 15.1912 2.94199 15.415L3.39598 14.5239ZM2.66797 6.55486H1.66797V12.5359H2.66797H3.66797V6.55486H2.66797ZM11.2013 1.33594V0.335938H7.88692V1.33594V2.33594H11.2013V1.33594ZM3.0585 5.61205L3.7656 6.31916L7.65122 2.43357L6.94411 1.72646L6.23701 1.01935L2.35139 4.90494L3.0585 5.61205ZM3.33464 6.0026V7.0026H6.61669V6.0026V5.0026H3.33464V6.0026ZM7.33464 5.28465H8.33464V2.0026H7.33464H6.33464V5.28465H7.33464ZM6.61669 6.0026V7.0026C7.56549 7.0026 8.33464 6.23344 8.33464 5.28465H7.33464H6.33464C6.33464 5.12889 6.46091 5.0026 6.61669 5.0026V6.0026ZM7.88692 1.33594V0.335938C7.55024 0.335938 7.22102 0.408722 6.92044 0.54551L7.33464 1.4557L7.74884 2.36588C7.79185 2.34631 7.83882 2.33594 7.88692 2.33594V1.33594ZM7.33464 1.4557L6.92044 0.54551C6.66849 0.660164 6.43651 0.819847 6.23701 1.01935L6.94411 1.72646L7.65122 2.43357C7.67974 2.40505 7.71282 2.38227 7.74884 2.36588L7.33464 1.4557ZM7.33464 2.0026H8.33464V1.4557H7.33464H6.33464V2.0026H7.33464ZM2.66797 6.55486H3.66797C3.66797 6.50677 3.67834 6.45979 3.69791 6.41679L2.78772 6.0026L1.87752 5.58842C1.74075 5.889 1.66797 6.2182 1.66797 6.55486H2.66797ZM2.78772 6.0026L3.69791 6.41679C3.7143 6.38077 3.73708 6.34768 3.7656 6.31916L3.0585 5.61205L2.35139 4.90494C2.15187 5.10446 1.99218 5.33646 1.87752 5.58842L2.78772 6.0026ZM3.33464 6.0026V5.0026H2.78772V6.0026V7.0026H3.33464V6.0026Z" fill="#7c3aed"/>
                      </svg>
                      {t('SAVE_DRAFT')}
                    </button>
                  </div>
                </div>
              ) : verificationMode === null ? (
                /* ── Selection screen (tab-style cards) ── */
                <div className="idv-select" style={{ padding: '4px 0 8px' }}>
                  <p className="idv-select-title">{t('CHOOSE_VERIFICATION_METHOD')}</p>
                  <p className="idv-select-desc">{t('ID_VERIFY_METHOD_INTRO')}</p>
                  <div className={`idv-tabs${canVerifyViaBankId === false ? ' idv-tabs--single' : ''}`} role="tablist">
                    {canVerifyViaBankId !== false && (
                      <button
                        type="button"
                        role="tab"
                        className="idv-tab"
                        onClick={() => setVerificationMode('bankid')}
                      >
                        <div className="option-icon">
                          <img src="/assets/svg/bankid-vector-logo-color.svg" alt="BankID" width="28" height="28" />
                        </div>
                        <div className="idv-tab-text">
                          <h3>{t('VERIFY_WITH_BANKID')}</h3>
                          <span>{t('VERIFY_WITH_BANKID_SUBTITLE')}</span>
                        </div>
                      </button>
                    )}
                    <button
                      type="button"
                      role="tab"
                      className="idv-tab"
                      onClick={() => setVerificationMode('document')}
                    >
                      <div className="option-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="14,2 14,8 20,8" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="12" y1="11" x2="12" y2="17" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round"/>
                          <polyline points="9,14 12,11 15,14" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="idv-tab-text">
                        <h3>{t('UPLOAD_ID_DOCUMENT')}</h3>
                        <span>PDF, PNG, JPG · max 5 MB</span>
                      </div>
                    </button>
                  </div>
                </div>
              ) : verificationMode === 'bankid' ? (
                /* ── BankID mode: Save Draft option shown below BankID buttons ── */
                <div style={{
                  marginTop: '16px', paddingTop: '16px',
                  borderTop: '1px solid #ede9fb', textAlign: 'center',
                  background: 'linear-gradient(135deg,#fdfcff,#f7f3ff)',
                  borderRadius: '12px', padding: '16px 14px',
                }}>
                  <p style={{ fontSize: '13px', color: '#8b7cc8', margin: '0 0 12px', lineHeight: 1.55, fontWeight: 500 }}>
                    {t('SAVE_DRAFT_WHILE_WAITING')}
                  </p>
                  <button
                    type="button"
                    onClick={handleBankIdDraftSaveClick}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1.5px solid #7c3aed',
                      background: '#fff',
                      color: '#7c3aed',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16 16" fill="none">
                      <path d="M13.1893 2.06395L14.0803 1.60996L14.0803 1.60996L13.1893 2.06395ZM11.2013 1.33594L11.2013 2.33594L11.2013 2.33594L11.2013 1.33594ZM12.6066 1.48126L13.0606 0.590257L13.0606 0.590256L12.6066 1.48126ZM12.6066 14.5239L13.0606 15.415L13.0606 15.415L12.6066 14.5239ZM13.1893 13.9413L14.0803 14.3952L14.0803 14.3952L13.1893 13.9413ZM3.39598 14.5239L2.94199 15.415L2.94199 15.415L3.39598 14.5239ZM2.81329 13.9413L3.7043 13.4873L3.7043 13.4873L2.81329 13.9413ZM3.0585 5.61205L2.35139 4.90494L2.35139 4.90494L3.0585 5.61205ZM6.94411 1.72646L6.23701 1.01935L6.23701 1.01935L6.94411 1.72646ZM4.8013 14.6693V15.6693H11.2013V14.6693V13.6693H4.8013V14.6693ZM13.3346 12.5359H14.3346V3.46927H13.3346H12.3346V12.5359H13.3346ZM13.3346 3.46927H14.3346C14.3346 3.1124 14.3354 2.78461 14.3131 2.51211C14.2899 2.22793 14.2368 1.91704 14.0803 1.60996L13.1893 2.06395L12.2983 2.51794C12.2872 2.49608 12.3067 2.51448 12.3198 2.67497C12.3339 2.84714 12.3346 3.0794 12.3346 3.46927H13.3346ZM11.2013 1.33594L11.2013 2.33594C11.5912 2.33594 11.8234 2.33672 11.9956 2.35078C12.1561 2.36389 12.1745 2.38341 12.1526 2.37227L12.6066 1.48126L13.0606 0.590256C12.7535 0.433793 12.4426 0.380642 12.1585 0.357424C11.886 0.33516 11.5582 0.335937 11.2013 0.335938L11.2013 1.33594ZM13.1893 2.06395L14.0803 1.60996C13.8566 1.17091 13.4997 0.81396 13.0606 0.590257L12.6066 1.48126L12.1526 2.37227C12.2154 2.40423 12.2663 2.45522 12.2983 2.51794L13.1893 2.06395ZM11.2013 14.6693V15.6693C11.5582 15.6693 11.886 15.67 12.1585 15.6478C12.4426 15.6246 12.7535 15.5714 13.0606 15.415L12.6066 14.5239L12.1526 13.6329C12.1745 13.6218 12.1561 13.6413 11.9956 13.6544C11.8234 13.6685 11.5912 13.6693 11.2013 13.6693V14.6693ZM13.3346 12.5359H12.3346C12.3346 12.9258 12.3339 13.1581 12.3198 13.3302C12.3067 13.4907 12.2872 13.5091 12.2983 13.4873L13.1893 13.9413L14.0803 14.3952C14.2368 14.0882 14.2899 13.7773 14.3131 13.4931C14.3354 13.2206 14.3346 12.8928 14.3346 12.5359H13.3346ZM12.6066 14.5239L13.0606 15.415C13.4997 15.1912 13.8566 14.8343 14.0803 14.3952L13.1893 13.9413L12.2983 13.4873C12.2663 13.55 12.2154 13.601 12.1526 13.6329L12.6066 14.5239ZM4.8013 14.6693V13.6693C4.41144 13.6693 4.17918 13.6685 4.00701 13.6544C3.8465 13.6413 3.82811 13.6218 3.84997 13.6329L3.39598 14.5239L2.94199 15.415C3.24907 15.5714 3.55997 15.6246 3.84415 15.6478C4.11665 15.67 4.44444 15.6693 4.8013 15.6693V14.6693ZM2.66797 12.5359H1.66797C1.66797 12.8928 1.66719 13.2206 1.68946 13.4931C1.71267 13.7773 1.76582 14.0882 1.92229 14.3952L2.81329 13.9413L3.7043 13.4873C3.71544 13.5091 3.69593 13.4907 3.68281 13.3302C3.66875 13.1581 3.66797 12.9258 3.66797 12.5359H2.66797ZM3.39598 14.5239L3.84997 13.6329C3.78725 13.601 3.73626 13.55 3.7043 13.4873L2.81329 13.9413L1.92229 14.3952C2.14599 14.8343 2.50295 15.1912 2.94199 15.415L3.39598 14.5239ZM2.66797 6.55486H1.66797V12.5359H2.66797H3.66797V6.55486H2.66797ZM11.2013 1.33594V0.335938H7.88692V1.33594V2.33594H11.2013V1.33594ZM3.0585 5.61205L3.7656 6.31916L7.65122 2.43357L6.94411 1.72646L6.23701 1.01935L2.35139 4.90494L3.0585 5.61205ZM3.33464 6.0026V7.0026H6.61669V6.0026V5.0026H3.33464V6.0026ZM7.33464 5.28465H8.33464V2.0026H7.33464H6.33464V5.28465H7.33464ZM6.61669 6.0026V7.0026C7.56549 7.0026 8.33464 6.23344 8.33464 5.28465H7.33464H6.33464C6.33464 5.12889 6.46091 5.0026 6.61669 5.0026V6.0026ZM7.88692 1.33594V0.335938C7.55024 0.335938 7.22102 0.408722 6.92044 0.54551L7.33464 1.4557L7.74884 2.36588C7.79185 2.34631 7.83882 2.33594 7.88692 2.33594V1.33594ZM7.33464 1.4557L6.92044 0.54551C6.66849 0.660164 6.43651 0.819847 6.23701 1.01935L6.94411 1.72646L7.65122 2.43357C7.67974 2.40505 7.71282 2.38227 7.74884 2.36588L7.33464 1.4557ZM7.33464 2.0026H8.33464V1.4557H7.33464H6.33464V2.0026H7.33464ZM2.66797 6.55486H3.66797C3.66797 6.50677 3.67834 6.45979 3.69791 6.41679L2.78772 6.0026L1.87752 5.58842C1.74075 5.889 1.66797 6.2182 1.66797 6.55486H2.66797ZM2.78772 6.0026L3.69791 6.41679C3.7143 6.38077 3.73708 6.34768 3.7656 6.31916L3.0585 5.61205L2.35139 4.90494C2.15187 5.10446 1.99218 5.33646 1.87752 5.58842L2.78772 6.0026ZM3.33464 6.0026V5.0026H2.78772V6.0026V7.0026H3.33464V6.0026Z" fill="#7c3aed"/>
                    </svg>
                    {t('SAVE_DRAFT')}
                  </button>
                </div>
              ) : (
                /* ── Document upload mode ── */
                (() => {
                  // Per-document status helpers
                  const idDoc = bankidDocuments?.[0];
                  const addressDoc = bankidDocuments?.[1];
                  const idDocApproved = idDoc?.status === 1;
                  const addressDocApproved = addressDoc?.status === 1;
                  const idDocRejected = idDoc?.status === 2;
                  const addressDocRejected = addressDoc?.status === 2;

                  // A slot is "satisfied" if approved from server OR user just uploaded a new file
                  const idDocSatisfied = idDocApproved || (!!verificationDocument && !!verificationDocumentType);
                  const addressDocSatisfied = addressDocApproved || (!!addressProofDocument && !!addressProofDocumentName.trim());

                  // Only show submit if at least one doc still needs uploading
                  const anyNeedsUpload = !idDocApproved || !addressDocApproved;
                  const submitDisabled = !idDocSatisfied || !addressDocSatisfied || verificationDocumentSubmitting;

                  return (
                  <>
                  {/* ── Document Type selector (only show if ID doc not yet approved) ── */}
                  {!idDocApproved && (
                    <div className="bankid-option" style={{ cursor: 'default', flexDirection: 'column', alignItems: 'stretch', gap: '8px', background: 'linear-gradient(135deg,#fdfcff,#f7f3ff)' }}>
                      <label style={{ fontSize: '13px', fontWeight: 600, color: '#7c3aed', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="#7c3aed" strokeWidth="2"/><path d="M8 12h8M8 8h5" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round"/></svg>
                        {t('ID_DOCUMENT_TYPE_LABEL')} <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {[
                          { value: 'passport', label: t('ID_DOCUMENT_TYPE_PASSPORT'), icon: '🛂' },
                          { value: 'national_id', label: t('ID_DOCUMENT_TYPE_NATIONAL_ID'), icon: '🪪' },
                        ].map((opt) => {
                          const isSelected = verificationDocumentType === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => { setVerificationDocumentType(opt.value); setVerificationDocumentName(''); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                padding: '11px 14px', borderRadius: '10px',
                                border: '2px solid',
                                borderColor: isSelected ? '#7c3aed' : '#e5e7eb',
                                backgroundColor: isSelected ? '#ede8fc' : '#fff',
                                cursor: 'pointer',
                                transition: [
                                  'border-color 0.32s cubic-bezier(0.22,1,0.36,1)',
                                  'background-color 0.32s cubic-bezier(0.22,1,0.36,1)',
                                  'box-shadow 0.32s cubic-bezier(0.22,1,0.36,1)',
                                ].join(','),
                                boxShadow: isSelected ? '0 3px 14px rgba(103,80,184,0.18)' : '0 1px 3px rgba(0,0,0,0.04)',
                                textAlign: 'left', width: '100%',
                                willChange: 'border-color, background-color, box-shadow',
                              }}
                            >
                              <span style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '42px', height: '42px', borderRadius: '12px', flexShrink: 0, fontSize: '24px', lineHeight: 1,
                                backgroundColor: isSelected ? 'rgba(124,58,237,0.18)' : 'rgba(124,58,237,0.10)',
                                transition: 'background-color 0.32s cubic-bezier(0.22,1,0.36,1)',
                              }}>{opt.icon}</span>
                              <span style={{
                                flex: 1, fontSize: '14px', fontWeight: 600,
                                color: isSelected ? '#7c3aed' : '#374151',
                                transition: 'color 0.32s cubic-bezier(0.22,1,0.36,1)',
                              }}>{opt.label}</span>
                              <span style={{
                                width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                                border: '2px solid',
                                borderColor: isSelected ? '#7c3aed' : '#d1d5db',
                                backgroundColor: '#fff',
                                boxShadow: isSelected ? 'inset 0 0 0 4px #7c3aed' : 'none',
                                display: 'inline-block',
                                transition: [
                                  'border-color 0.32s cubic-bezier(0.22,1,0.36,1)',
                                  'box-shadow 0.32s cubic-bezier(0.22,1,0.36,1)',
                                ].join(','),
                              }} />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ── ID Document upload area ── */}
                  <input
                    ref={verificationDocInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    style={{ display: 'none' }}
                    onChange={handleVerificationDocumentChange}
                  />
                  {idDocApproved ? (
                    /* ID doc approved by admin — green card, no upload */
                    <div className="bankid-option" style={{ cursor: 'default', borderColor: '#10b981', background: 'linear-gradient(135deg,#f0fdf4,#ecfdf5)' }}>
                      <div className="option-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" fill="#d1fae5" stroke="#10b981" strokeWidth="1.5"/>
                          <polyline points="7,12 10,15 17,9" stroke="#10b981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="option-content">
                        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#065f46' }}>
                          ✅ {t('UPLOAD_ID_DOCUMENT')} — {t('ID_DOCUMENT_APPROVED_LABEL')}
                        </h3>
                        <span style={{ fontSize: '12px', color: '#059669', fontWeight: 500 }}>{idDoc?.documentName}</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Per-doc rejection banner for ID doc */}
                      {idDocRejected && idDoc?.rejectionReason && (
                        <div style={{
                          background: '#fef2f2', border: '1.5px solid #fca5a5', borderLeft: '4px solid #ef4444',
                          borderRadius: '10px', padding: '10px 12px', marginBottom: '6px',
                          display: 'flex', gap: '8px', alignItems: 'flex-start',
                        }}>
                          <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
                          <div>
                            <p style={{ margin: '0 0 2px', fontSize: '12px', fontWeight: 700, color: '#dc2626' }}>
                              {t('ID_DOCUMENT_REJECTED_LABEL')}
                            </p>
                            <p style={{ margin: 0, fontSize: '12px', color: '#7f1d1d', lineHeight: 1.5 }}>
                              {idDoc.rejectionReason}
                            </p>
                          </div>
                        </div>
                      )}
                      {!verificationDocument ? (
                        <button type="button" className="bankid-option" onClick={() => verificationDocInputRef.current?.click()}>
                          <div className="option-icon">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <polyline points="14,2 14,8 20,8" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <line x1="12" y1="11" x2="12" y2="17" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round"/>
                              <polyline points="9,14 12,11 15,14" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div className="option-content">
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 500, color: '#000' }}>
                              {idDocRejected ? t('ID_DOCUMENT_REUPLOAD_LABEL') : t('UPLOAD_ID_DOCUMENT')}
                            </h3>
                            <span style={{ fontSize: '12px', color: '#6B7280' }}>PDF, PNG, JPG · max 5 MB</span>
                          </div>
                        </button>
                      ) : (
                        <div className="bankid-option" style={{ cursor: 'default', borderColor: '#10b981' }}>
                          <div className="option-icon">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <polyline points="14,2 14,8 20,8" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <polyline points="9,12 11,14 15,10" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div className="option-content">
                            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 500, color: '#10b981', wordBreak: 'break-all' }}>
                              {verificationDocumentPreview}
                            </h3>
                            <button type="button" onClick={handleVerificationDocumentRemove}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', color: '#6B7280', fontSize: '12px' }}>
                              ✕
                            </button>
                          </div>
                        </div>
                      )}
                      {verificationDocumentError && (
                        <p style={{ margin: '6px 0 0 4px', fontSize: '13px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontWeight: 600 }}>⚠</span> {verificationDocumentError}
                        </p>
                      )}
                    </>
                  )}

                  {/* ── Address Proof Document ── */}
                  <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1.5px dashed #ede9fb' }}>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#374151', margin: '0 0 8px' }}>
                      {t('ADDRESS_PROOF_DOCUMENT_LABEL')} <span style={{ color: '#ef4444' }}>*</span>
                    </p>
                    <input
                      ref={addressProofDocInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      style={{ display: 'none' }}
                      onChange={handleAddressProofDocumentChange}
                    />
                    {addressDocApproved ? (
                      /* Address proof approved by admin — green card, no upload */
                      <div className="bankid-option" style={{ cursor: 'default', borderColor: '#10b981', background: 'linear-gradient(135deg,#f0fdf4,#ecfdf5)' }}>
                        <div className="option-icon">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" fill="#d1fae5" stroke="#10b981" strokeWidth="1.5"/>
                            <polyline points="7,12 10,15 17,9" stroke="#10b981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="option-content">
                          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#065f46' }}>
                            ✅ {t('ADDRESS_PROOF_APPROVED_LABEL')}
                          </h3>
                          <span style={{ fontSize: '12px', color: '#059669', fontWeight: 500 }}>{addressDoc?.documentName}</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Per-doc rejection banner for address proof */}
                        {addressDocRejected && addressDoc?.rejectionReason && (
                          <div style={{
                            background: '#fef2f2', border: '1.5px solid #fca5a5', borderLeft: '4px solid #ef4444',
                            borderRadius: '10px', padding: '10px 12px', marginBottom: '6px',
                            display: 'flex', gap: '8px', alignItems: 'flex-start',
                          }}>
                            <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
                            <div>
                              <p style={{ margin: '0 0 2px', fontSize: '12px', fontWeight: 700, color: '#dc2626' }}>
                                {t('ADDRESS_PROOF_REJECTED_LABEL')}
                              </p>
                              <p style={{ margin: 0, fontSize: '12px', color: '#7f1d1d', lineHeight: 1.5 }}>
                                {addressDoc.rejectionReason}
                              </p>
                            </div>
                          </div>
                        )}
                        {!addressProofDocument ? (
                          <button type="button" className="bankid-option" onClick={() => addressProofDocInputRef.current?.click()}>
                            <div className="option-icon">
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="14,2 14,8 20,8" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <line x1="12" y1="11" x2="12" y2="17" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round"/>
                                <polyline points="9,14 12,11 15,14" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div className="option-content">
                              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 500, color: '#000' }}>
                                {addressDocRejected ? t('ADDRESS_PROOF_REUPLOAD_LABEL') : t('ADDRESS_PROOF_UPLOAD_LABEL')}
                              </h3>
                              <span style={{ fontSize: '12px', color: '#6B7280' }}>PDF, PNG, JPG · max 5 MB</span>
                            </div>
                          </button>
                        ) : (
                          <div className="bankid-option" style={{ cursor: 'default', borderColor: '#10b981' }}>
                            <div className="option-icon">
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="14,2 14,8 20,8" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="9,12 11,14 15,10" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div className="option-content">
                              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 500, color: '#10b981', wordBreak: 'break-all' }}>
                                {addressProofDocumentPreview}
                              </h3>
                              <button type="button" onClick={handleAddressProofDocumentRemove}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', color: '#6B7280', fontSize: '12px' }}>
                                ✕
                              </button>
                            </div>
                          </div>
                        )}
                        {addressProofDocumentError && (
                          <p style={{ margin: '6px 0 0 4px', fontSize: '13px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontWeight: 600 }}>⚠</span> {addressProofDocumentError}
                          </p>
                        )}
                        <div style={{ marginTop: '8px' }}>
                          <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px', display: 'block' }}>
                            {t('ADDRESS_PROOF_DOCUMENT_NAME_LABEL')} <span style={{ color: '#ef4444' }}>*</span>
                          </label>
                          <input
                            type="text"
                            value={addressProofDocumentName}
                            onChange={(e) => setAddressProofDocumentName(e.target.value)}
                            placeholder={t('ADDRESS_PROOF_DOCUMENT_NAME_PLACEHOLDER') as string}
                            style={{
                              width: '100%', padding: '11px 14px',
                              border: '1.5px solid #e6e0f7', borderRadius: '10px',
                              fontSize: '14px', color: '#111827', outline: 'none', background: '#fff',
                              transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
                            }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = '#e6e0f7'; e.currentTarget.style.boxShadow = 'none'; }}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* ── Submit button (only shown when at least one doc still needs uploading) ── */}
                  {anyNeedsUpload && (
                    <button
                      type="button"
                      disabled={submitDisabled}
                      onClick={handleVerificationDocumentSubmit}
                      style={{
                        marginTop: '14px', width: '100%', padding: '13px',
                        borderRadius: '999px', border: 'none',
                        background: submitDisabled ? '#ece9f6' : 'linear-gradient(135deg,#7c3aed,#a855f7)',
                        color: submitDisabled ? '#a49ec0' : '#fff',
                        fontSize: '15px', fontWeight: 700,
                        cursor: submitDisabled ? 'not-allowed' : 'pointer',
                        boxShadow: submitDisabled ? 'none' : '0 12px 26px -12px rgba(124,58,237,0.75)',
                        transition: 'transform 0.18s cubic-bezier(0.22,1,0.36,1), box-shadow 0.18s ease, filter 0.18s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (submitDisabled) return;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.filter = 'brightness(1.04)';
                        e.currentTarget.style.boxShadow = '0 18px 34px -12px rgba(124,58,237,0.85)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.filter = 'none';
                        e.currentTarget.style.boxShadow = submitDisabled ? 'none' : '0 12px 26px -12px rgba(124,58,237,0.75)';
                      }}
                    >
                      {verificationDocumentSubmitting ? '...' : t('SUBMIT_ID_DOCUMENT')}
                    </button>
                  )}
                  <div style={{
                    marginTop: '16px', borderTop: '1px solid #ede9fb', textAlign: 'center',
                    background: 'linear-gradient(135deg,#fdfcff,#f7f3ff)',
                    borderRadius: '12px', padding: '16px 14px',
                  }}>
                    <p style={{ fontSize: '13px', color: '#8b7cc8', margin: '0 0 12px', lineHeight: 1.55, fontWeight: 500 }}>
                      {t('SAVE_DRAFT_WHILE_WAITING')}
                    </p>
                    <button
                      type="button"
                      onClick={handleBankIdDraftSaveClick}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '8px 16px', borderRadius: '8px',
                        border: '1.5px solid #7c3aed', background: '#fff',
                        color: '#7c3aed', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16 16" fill="none">
                        <path d="M13.1893 2.06395L14.0803 1.60996L14.0803 1.60996L13.1893 2.06395ZM11.2013 1.33594L11.2013 2.33594L11.2013 2.33594L11.2013 1.33594ZM12.6066 1.48126L13.0606 0.590257L13.0606 0.590256L12.6066 1.48126ZM12.6066 14.5239L13.0606 15.415L13.0606 15.415L12.6066 14.5239ZM13.1893 13.9413L14.0803 14.3952L14.0803 14.3952L13.1893 13.9413ZM3.39598 14.5239L2.94199 15.415L2.94199 15.415L3.39598 14.5239ZM2.81329 13.9413L3.7043 13.4873L3.7043 13.4873L2.81329 13.9413ZM3.0585 5.61205L2.35139 4.90494L2.35139 4.90494L3.0585 5.61205ZM6.94411 1.72646L6.23701 1.01935L6.23701 1.01935L6.94411 1.72646ZM4.8013 14.6693V15.6693H11.2013V14.6693V13.6693H4.8013V14.6693ZM13.3346 12.5359H14.3346V3.46927H13.3346H12.3346V12.5359H13.3346ZM13.3346 3.46927H14.3346C14.3346 3.1124 14.3354 2.78461 14.3131 2.51211C14.2899 2.22793 14.2368 1.91704 14.0803 1.60996L13.1893 2.06395L12.2983 2.51794C12.2872 2.49608 12.3067 2.51448 12.3198 2.67497C12.3339 2.84714 12.3346 3.0794 12.3346 3.46927H13.3346ZM11.2013 1.33594L11.2013 2.33594C11.5912 2.33594 11.8234 2.33672 11.9956 2.35078C12.1561 2.36389 12.1745 2.38341 12.1526 2.37227L12.6066 1.48126L13.0606 0.590256C12.7535 0.433793 12.4426 0.380642 12.1585 0.357424C11.886 0.33516 11.5582 0.335937 11.2013 0.335938L11.2013 1.33594ZM13.1893 2.06395L14.0803 1.60996C13.8566 1.17091 13.4997 0.81396 13.0606 0.590257L12.6066 1.48126L12.1526 2.37227C12.2154 2.40423 12.2663 2.45522 12.2983 2.51794L13.1893 2.06395ZM11.2013 14.6693V15.6693C11.5582 15.6693 11.886 15.67 12.1585 15.6478C12.4426 15.6246 12.7535 15.5714 13.0606 15.415L12.6066 14.5239L12.1526 13.6329C12.1745 13.6218 12.1561 13.6413 11.9956 13.6544C11.8234 13.6685 11.5912 13.6693 11.2013 13.6693V14.6693ZM13.3346 12.5359H12.3346C12.3346 12.9258 12.3339 13.1581 12.3198 13.3302C12.3067 13.4907 12.2872 13.5091 12.2983 13.4873L13.1893 13.9413L14.0803 14.3952C14.2368 14.0882 14.2899 13.7773 14.3131 13.4931C14.3354 13.2206 14.3346 12.8928 14.3346 12.5359H13.3346ZM12.6066 14.5239L13.0606 15.415C13.4997 15.1912 13.8566 14.8343 14.0803 14.3952L13.1893 13.9413L12.2983 13.4873C12.2663 13.55 12.2154 13.601 12.1526 13.6329L12.6066 14.5239ZM4.8013 14.6693V13.6693C4.41144 13.6693 4.17918 13.6685 4.00701 13.6544C3.8465 13.6413 3.82811 13.6218 3.84997 13.6329L3.39598 14.5239L2.94199 15.415C3.24907 15.5714 3.55997 15.6246 3.84415 15.6478C4.11665 15.67 4.44444 15.6693 4.8013 15.6693V14.6693ZM2.66797 12.5359H1.66797C1.66797 12.8928 1.66719 13.2206 1.68946 13.4931C1.71267 13.7773 1.76582 14.0882 1.92229 14.3952L2.81329 13.9413L3.7043 13.4873C3.71544 13.5091 3.69593 13.4907 3.68281 13.3302C3.66875 13.1581 3.66797 12.9258 3.66797 12.5359H2.66797ZM3.39598 14.5239L3.84997 13.6329C3.78725 13.601 3.73626 13.55 3.7043 13.4873L2.81329 13.9413L1.92229 14.3952C2.14599 14.8343 2.50295 15.1912 2.94199 15.415L3.39598 14.5239ZM2.66797 6.55486H1.66797V12.5359H2.66797H3.66797V6.55486H2.66797ZM11.2013 1.33594V0.335938H7.88692V1.33594V2.33594H11.2013V1.33594ZM3.0585 5.61205L3.7656 6.31916L7.65122 2.43357L6.94411 1.72646L6.23701 1.01935L2.35139 4.90494L3.0585 5.61205ZM3.33464 6.0026V7.0026H6.61669V6.0026V5.0026H3.33464V6.0026ZM7.33464 5.28465H8.33464V2.0026H7.33464H6.33464V5.28465H7.33464ZM6.61669 6.0026V7.0026C7.56549 7.0026 8.33464 6.23344 8.33464 5.28465H7.33464H6.33464C6.33464 5.12889 6.46091 5.0026 6.61669 5.0026V6.0026ZM7.88692 1.33594V0.335938C7.55024 0.335938 7.22102 0.408722 6.92044 0.54551L7.33464 1.4557L7.74884 2.36588C7.79185 2.34631 7.83882 2.33594 7.88692 2.33594V1.33594ZM7.33464 1.4557L6.92044 0.54551C6.66849 0.660164 6.43651 0.819847 6.23701 1.01935L6.94411 1.72646L7.65122 2.43357C7.67974 2.40505 7.71282 2.38227 7.74884 2.36588L7.33464 1.4557ZM7.33464 2.0026H8.33464V1.4557H7.33464H6.33464V2.0026H7.33464ZM2.66797 6.55486H3.66797C3.66797 6.50677 3.67834 6.45979 3.69791 6.41679L2.78772 6.0026L1.87752 5.58842C1.74075 5.889 1.66797 6.2182 1.66797 6.55486H2.66797ZM2.78772 6.0026L3.69791 6.41679C3.7143 6.38077 3.73708 6.34768 3.7656 6.31916L3.0585 5.61205L2.35139 4.90494C2.15187 5.10446 1.99218 5.33646 1.87752 5.58842L2.78772 6.0026ZM3.33464 6.0026V5.0026H2.78772V6.0026V7.0026H3.33464V6.0026Z" fill="#7c3aed"/>
                      </svg>
                      {t('SAVE_DRAFT')}
                    </button>
                  </div>
                  </>
                  );
                })()
              )}
            </div>
          }
        />
      )}

      {/* Unsaved-changes warning modal (in-app navigation) */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        isSaving={isSavingDraft}
        onStay={() => {
          setShowUnsavedModal(false);
          pendingNavigationRef.current = null;
        }}
        onLeave={() => {
          setShowUnsavedModal(false);
          pendingNavigationRef.current?.();
          pendingNavigationRef.current = null;
        }}
        onSaveAndLeave={async () => {
          const ref = invoiceDataRef.current;
          // 1. Customer must be selected
          if (!ref?.formData?.customer?.id) {
            toast.error(String(t('DRAFT_REQUIRES_CUSTOMER')));
            setShowUnsavedModal(false);
            return;
          }
          // 2. At least one working hour row must exist
          const byFreelancer = ref?.serviceTableDataByFreelancer;
          const hasWorkingHours = !!(byFreelancer && Object.keys(byFreelancer).length > 0 &&
            Object.values(byFreelancer).some(
              (fd: any) => Array.isArray(fd?.workingHours) && fd.workingHours.length > 0
            ));
          if (!hasWorkingHours) {
            toast.error(String(t('DRAFT_REQUIRES_WORKING_HOURS')));
            setShowUnsavedModal(false);
            return;
          }
          // 3. Description must pass validation (20+ chars OR 3+ words)
          const description = (ref?.formData?.otherDetails?.customerNotes || '').trim();
          const wordCount = description.split(/\s+/).filter((w: string) => w.length > 0).length;
          if (!description || (description.length < 20 && wordCount < 3)) {
            toast.error(String(t('DRAFT_REQUIRES_DESCRIPTION')));
            setShowUnsavedModal(false);
            return;
          }
          // Capture the pending destination BEFORE clearing modal/refs
          const proceedToDestination = pendingNavigationRef.current;
          pendingNavigationRef.current = null;
          setIsSavingDraft(true);
          try {
            const payload = await buildInvoicePayload();
            if (!payload) {
              toast.error('Unable to build invoice payload');
              setIsSavingDraft(false);
              setShowUnsavedModal(false);
              return;
            }
            // Step 1: get preliminary invoice PDF
            const pdfBlob = await createPreliminaryInvoiceApi(payload);
            // Step 2: convert to base64 and save as draft
            const pdfBase64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64 = (reader.result as string).split(',')[1];
                resolve(base64);
              };
              reader.onerror = reject;
              reader.readAsDataURL(pdfBlob);
            });
            const draftPayload: CreateDraftInvoicePayload = { ...payload, invoicePdf: pdfBase64 };
            await dispatch(createDraftInvoice(draftPayload)).unwrap();
            toast.success(String(t('DRAFT_INVOICE_SAVED_SUCCESSFULLY')));
            setIsSavingDraft(false);
            setShowUnsavedModal(false);
            // Navigate to the page the user originally tried to go to
            if (proceedToDestination) {
              proceedToDestination();
            }
          } catch (error: any) {
            setIsSavingDraft(false);
            setShowUnsavedModal(false);
            const msg = error?.response?.data?.message || error?.message || 'Failed to save draft invoice';
            toast.error(msg);
          }
        }}
      />

      {/* First-visit welcome (offers the guided tour) — portals to body */}
      <InvoiceWelcomeModal
        show={showTourWelcome && isFreshInvoice}
        onStart={handleWelcomeStart}
        onIgnore={handleWelcomeIgnore}
        userName={userDetails?.firstName || userDetails?.name || ''}
      />

      {/* Guided tour overlay (portals to body) */}
      <InvoiceTour
        run={tourRun}
        stepIndex={tourStep}
        steps={tourSteps}
        canPrev={tourCanPrev}
        onPrev={tourPrev}
        onNext={tourNext}
        onClose={endTour}
      />
    </>
  );
};

export default CreateInvoicePage;
